import { KlarnaPaymentDialog } from "@/components/checkout/KlarnaPaymentDialog";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { StorefrontLayout } from "@/components/storefront/Layout";

import { useCart, groupCartRows } from "@/hooks/storefront/use-cart";
import { useCartPriceSync } from "@/hooks/storefront/use-cart-price-sync";
import { BundleCartGroup } from "@/components/storefront/BundleCartGroup";
import { useAppliedCoupon } from "@/hooks/storefront/use-coupon";
import { PENDING_COUPON_KEY } from "@/hooks/storefront/use-auto-apply-coupon";
import { useFragileProducts } from "@/hooks/storefront/use-fragile";
import { useAuth } from "@/hooks/use-auth";
import { createOrder, listMyAddresses, type SavedAddress } from "@/lib/orders.functions";
import { validateCoupon } from "@/lib/coupons.functions";
import { computeCartBundleDiscount } from "@/lib/mrbox";
import { migrateLegacyLogin } from "@/lib/legacy-auth.functions";
import { lookupPostalCode } from "@/lib/address.functions";
import { lookupVatNumber } from "@/lib/aade.functions";
import { quoteIslandVat } from "@/lib/island-vat.functions";
import { islandForPostalCode, type IslandVatQuote } from "@/lib/island-vat";
import {
  computeMrPandaShipping,
  computeEltaShipping,
  computeBoxnowShipping,
  computeSkroutzShipping,
  computeAcsShipping,
  computeCourierCenterShipping,
  type MrPandaShippingResult,
  type EltaShippingResult,
  type BoxnowShippingResult,
  type SkroutzShippingResult,
  type AcsShippingResult,
  type CourierCenterShippingResult,
} from "@/lib/shipping.functions";
import { BoxnowLockerModal, type PickedLocker } from "@/components/storefront/BoxnowLockerPicker";
import {
  SkroutzLockerModal,
  type PickedSkroutzLocker,
} from "@/components/storefront/SkroutzLockerPicker";
import {
  SkroutzLockerNotice,
  SKROUTZ_LOCKER_DIMS,
} from "@/components/storefront/SkroutzLockerNotice";
import { AddressAutocomplete } from "@/components/storefront/AddressAutocomplete";
import { TrustBadges } from "@/components/storefront/TrustBadges";
import { PickupScheduler, type PickupSlot } from "@/components/storefront/PickupScheduler";
import { PickupSmsNotice } from "@/components/storefront/PickupSmsNotice";
import { StorePickupInfoDialog } from "@/components/storefront/StorePickupInfoDialog";
import { DeliveryScheduler } from "@/components/storefront/DeliveryScheduler";
import { getPickupInfo, type PickupInfo } from "@/lib/pickup.functions";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice, productSlug } from "@/lib/format";
import { BANK_ACCOUNTS, BENEFICIARY } from "@/lib/bank-accounts";
import { GREEK_CITIES } from "@/lib/greek-cities";
import { startTiming } from "@/lib/user-timing";
import { orderNotesSchema } from "@/lib/order-notes";
import {
  GREEK_REGIONS,
  CYPRUS_DISTRICTS,
  COUNTRIES,
  regionForGreekPostalCode,
} from "@/lib/greek-regions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  isValidContactPhone,
  isLandlinePhone as isLandlineNumber,
  normalizePhoneDigits,
} from "@/lib/phone";

const PHONE_ERROR =
  "Δώσε έγκυρο τηλέφωνο: κινητό Ελλάδας 69… , σταθερό 2…/8… ή κυπριακό 9XXXXXXX / 2XXXXXXX.";
const isValidPhone = (v: string) => isValidContactPhone(v);
const isLandlinePhone = (v: string) => isLandlineNumber(v);
const LOCKER_METHODS = ["boxnow", "skroutz_last_mile"];

import storePickupLogo from "@/assets/couriers/store-pickup.webp";
import boxnowLogo from "@/assets/couriers/boxnow.webp";
import skroutzLastMileLogo from "@/assets/couriers/skroutz-last-mile.webp";
import acsLogo from "@/assets/couriers/acs.png";
import eltaLogo from "@/assets/couriers/elta.webp";
import courierCenterLogo from "@/assets/couriers/couriercenter.webp";
import mrpandaVanLogo from "@/assets/couriers/mrpanda-van.png";
import freightTruckLogo from "@/assets/couriers/freight-truck.png";
import vivaLogo from "@/assets/payments/viva.webp";
import klarnaLogo from "@/assets/payments/klarna.webp";
import irisLogo from "@/assets/payments/iris.webp";
import {
  CreditCard,
  Building2,
  Wallet,
  User,
  MapPin,
  ShoppingBag,
  Check,
  Eye,
  EyeOff,
  LogIn,
  Truck,
  ShieldCheck,
  Store,
  Package,
  PackageCheck,
  Send,
  Warehouse,
  Banknote,
  FileText,
  Receipt,
  MessageCircle,
  Copy,
  Search,
  Loader2,
  ArrowLeft,
} from "lucide-react";

export const Route = createFileRoute("/checkout")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://mrpanda.gr/checkout" },
      { title: "Ολοκλήρωση παραγγελίας — MrPanda.gr" },
      {
        name: "description",
        content:
          "Ολοκλήρωσε με ασφάλεια την παραγγελία σου στο MrPanda.gr με γρήγορη αποστολή και πολλούς τρόπους πληρωμής.",
      },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "Ολοκλήρωση παραγγελίας — MrPanda.gr" },
      { property: "og:description", content: "Ολοκλήρωσε με ασφάλεια την παραγγελία σου στο MrPanda.gr με γρήγορη αποστολή και πολλούς τρόπους πληρωμής." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: CheckoutPage,
});

type PaymentMethod = "viva" | "iris" | "klarna" | "bank_transfer" | "cash";

type DocumentType = "receipt" | "invoice";

type OutOfStockAction =
  | "replace_similar"
  | "contact_for_replacement"
  | "remove_and_ship"
  | "cancel_order";

const OUT_OF_STOCK_OPTIONS: { id: OutOfStockAction; label: string; desc: string }[] = [
  {
    id: "replace_similar",
    label: "Αντικατάσταση προϊόντος",
    desc: "Με παρόμοιο προϊόν στην ίδια περίπου τιμή",
  },
  {
    id: "contact_for_replacement",
    label: "Επικοινωνία για αντικατάσταση",
    desc: "Θα επικοινωνήσουμε μαζί σας για εναλλακτικό",
  },
  {
    id: "remove_and_ship",
    label: "Αφαίρεση προϊόντος",
    desc: "Αφαίρεση από την παραγγελία και αποστολή της υπόλοιπης",
  },
  {
    id: "cancel_order",
    label: "Ακύρωση όλης της παραγγελίας",
    desc: "Ακύρωση σε περίπτωση που λείπει κάποιο προϊόν",
  },
];

const METHODS: {
  id: PaymentMethod;
  label: string;
  desc: string;
  icon: typeof CreditCard;
  disabled?: boolean;
}[] = [
  { id: "viva", label: "Viva.com", desc: "Με κάρτα", icon: CreditCard },
  {
    id: "iris",
    label: "IRIS",
    desc: "Άμεση πληρωμή από τον τραπεζικό σου λογαριασμό",
    icon: Wallet,
  },
  {
    id: "klarna",
    label: "Klarna",
    desc: "Απευθείας με Klarna — οι διαθέσιμες επιλογές εμφανίζονται πριν την πληρωμή",
    icon: CreditCard,
  },
  {
    id: "bank_transfer",
    label: "Τραπεζικό έμβασμα",
    desc: "Θα λάβεις τα IBAN στο email",
    icon: Building2,
  },
  {
    id: "cash",
    label: "Μετρητά",
    desc: "Πληρωμή με μετρητά κατά την παραλαβή/παράδοση",
    icon: Banknote,
  },
];

const PAYMENT_LOGOS: Partial<Record<PaymentMethod, string>> = {
  viva: vivaLogo,
  iris: irisLogo,
  klarna: klarnaLogo,
};

type ShippingMethod =
  | "store_pickup"
  | "boxnow"
  | "skroutz_last_mile"
  | "acs"
  | "elta_courier"
  | "courier_center"
  | "mrpanda_delivery"
  | "customer_freight";

const SHIPPING_LOGOS: Record<ShippingMethod, string> = {
  store_pickup: storePickupLogo,
  boxnow: boxnowLogo,
  skroutz_last_mile: skroutzLastMileLogo,
  acs: acsLogo,
  elta_courier: eltaLogo,
  courier_center: courierCenterLogo,
  mrpanda_delivery: mrpandaVanLogo,
  customer_freight: freightTruckLogo,
};

const SHIPPING_METHODS: {
  id: ShippingMethod;
  label: string;
  desc: string;
  icon: typeof CreditCard;
}[] = [
  {
    id: "store_pickup",
    label: "Παραλαβή από το κατάστημα",
    desc: "Παράλαβε την παραγγελία σου από το κατάστημα",
    icon: Store,
  },
  {
    id: "boxnow",
    label: "BoxNow",
    desc: "Παράδοση σε αυτόματο μηχάνημα παραλαβής (locker)",
    icon: Package,
  },
  {
    id: "skroutz_last_mile",
    label: "Skroutz Last Mile",
    desc: "Παράδοση σε αυτόματο μηχάνημα παραλαβής (locker) — θυρίδα έως 58x50x44cm",
    icon: PackageCheck,
  },
  {
    id: "acs",
    label: "ACS Courier",
    desc: "Παράδοση με ACS — χρέωση βάσει βάρους & προορισμού",
    icon: Truck,
  },
  { id: "elta_courier", label: "ΕΛΤΑ Courier", desc: "Παράδοση με ΕΛΤΑ Courier", icon: Truck },
  {
    id: "courier_center",
    label: "Courier Center",
    desc: "Παράδοση με Courier Center — Ελλάδα & Κύπρος",
    icon: Truck,
  },
  {
    id: "mrpanda_delivery",
    label: "Παράδοση MrPanda",
    desc: "Παράδοση με δικό μας μεταφορικό μέσο 🐼",
    icon: Send,
  },
  {
    id: "customer_freight",
    label: "Παράδοση σε μεταφορική του πελάτη",
    desc: "Αποστολή στη μεταφορική εταιρεία που θα μας υποδείξεις",
    icon: Warehouse,
  },
];

function CheckoutPage() {
  const { items, subtotal, shipping, total, depositTotal, remove, syncPrices, clear } = useCart();
  // Οι τιμές του καλαθιού ανανεώνονται από τον server πριν την πληρωμή, ώστε το
  // ποσό στο ταμείο να είναι πάντα ίδιο με το ποσό που θα χρεωθεί.
  useCartPriceSync(items, syncPrices);
  const { user } = useAuth();
  const fragile = useFragileProducts();
  const navigate = useNavigate();
  const createOrderFn = useServerFn(createOrder);
  const migrate = useServerFn(migrateLegacyLogin);
  const fetchMyAddresses = useServerFn(listMyAddresses);
  const fetchCityByPostal = useServerFn(lookupPostalCode);
  const computeShipping = useServerFn(computeMrPandaShipping);
  const computeElta = useServerFn(computeEltaShipping);
  const computeBoxnow = useServerFn(computeBoxnowShipping);
  const computeSkroutz = useServerFn(computeSkroutzShipping);
  const computeAcs = useServerFn(computeAcsShipping);
  const computeCourierCenter = useServerFn(computeCourierCenterShipping);
  const fetchVatDetails = useServerFn(lookupVatNumber);
  const fetchIslandVat = useServerFn(quoteIslandVat);
  const fetchPickupInfo = useServerFn(getPickupInfo);
  const validateCouponFn = useServerFn(validateCoupon);

  const [klarnaPayment, setKlarnaPayment] = useState<{
    guestToken: string; clientToken: string; categories: string[];
  } | null>(null);
  const [method, setMethod] = useState<PaymentMethod>("viva");
  const [selectedBank, setSelectedBank] = useState("");
  const [copiedIban, setCopiedIban] = useState("");
  const [docType, setDocType] = useState<DocumentType>("receipt");
  const [vatNumber, setVatNumber] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyProfession, setCompanyProfession] = useState("");
  const [companyDoy, setCompanyDoy] = useState("");
  const [aadeLoading, setAadeLoading] = useState(false);
  const [aadeMsg, setAadeMsg] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const [islandVat, setIslandVat] = useState<IslandVatQuote | null>(null);
  // Τ.Κ. έδρας του ΑΦΜ όπως επιστράφηκε από την ΑΑΔΕ (ανεξάρτητος από τον Τ.Κ.
  // παράδοσης, τον οποίο ο πελάτης μπορεί να αλλάξει).
  const [aadePostalCode, setAadePostalCode] = useState("");
  const [outOfStockAction, setOutOfStockAction] = useState<OutOfStockAction | "">("");
  const [shippingMethod, setShippingMethod] = useState<ShippingMethod>("acs");
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(() => {
    if (typeof window === "undefined") return 1;
    const saved = Number(window.localStorage.getItem("mp_checkout_active_step"));
    return Number.isFinite(saved) && saved >= 1 && saved <= 4 ? saved : 1;
  });
  const [maxStep, setMaxStep] = useState(() => {
    if (typeof window === "undefined") return 1;
    const saved = Number(window.localStorage.getItem("mp_checkout_max_step"));
    return Number.isFinite(saved) && saved >= 1 && saved <= 4 ? saved : 1;
  });

  // Persist the checkout step across page reloads / tab close so the user
  // returns to the step they were on instead of starting from step 1.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("mp_checkout_active_step", String(activeStep));
  }, [activeStep]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("mp_checkout_max_step", String(maxStep));
  }, [maxStep]);
  const [mrpanda, setMrpanda] = useState<MrPandaShippingResult | null>(null);
  const [mrpandaLoading, setMrpandaLoading] = useState(false);
  const [elta, setElta] = useState<EltaShippingResult | null>(null);
  const [eltaLoading, setEltaLoading] = useState(false);
  const [acs, setAcs] = useState<AcsShippingResult | null>(null);
  const [acsLoading, setAcsLoading] = useState(false);
  const [courierCenter, setCourierCenter] = useState<CourierCenterShippingResult | null>(null);
  const [courierCenterLoading, setCourierCenterLoading] = useState(false);
  const [boxnow, setBoxnow] = useState<BoxnowShippingResult | null>(null);
  const [boxnowLoading, setBoxnowLoading] = useState(false);
  const [locker, setLocker] = useState<PickedLocker | null>(null);
  const [lockerOpen, setLockerOpen] = useState(false);
  const [skroutz, setSkroutz] = useState<SkroutzShippingResult | null>(null);
  const [skroutzLoading, setSkroutzLoading] = useState(false);
  const [skroutzLocker, setSkroutzLocker] = useState<PickedSkroutzLocker | null>(null);
  const [skroutzLockerOpen, setSkroutzLockerOpen] = useState(false);
  const [skroutzNoticeOpen, setSkroutzNoticeOpen] = useState(false);
  const [skroutzNoticeAccepted, setSkroutzNoticeAccepted] = useState(false);
  const [landlineNoticeOpen, setLandlineNoticeOpen] = useState(false);

  const [freightCarrier, setFreightCarrier] = useState("");
  const [freightNeedsSuggestion, setFreightNeedsSuggestion] = useState(false);
  const [freightConfirmed, setFreightConfirmed] = useState(false);
  const [courierNotes, setCourierNotes] = useState("");
  const [pickupInfo, setPickupInfo] = useState<PickupInfo | null>(null);
  const [pickupSlot, setPickupSlot] = useState<PickupSlot | null>(null);
  const [pickupError, setPickupError] = useState(false);
  const [pickupInfoOpen, setPickupInfoOpen] = useState(false);
  const [deliverySlot, setDeliverySlot] = useState<PickupSlot | null>(null);
  const [deliveryError, setDeliveryError] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Discount coupon state
  const [couponInput, setCouponInput] = useState("");
  const { coupon, setCoupon } = useAppliedCoupon();
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponMsg, setCouponMsg] = useState<{ type: "ok" | "error"; text: string } | null>(null);

  // Φόρτωσε τις πληροφορίες διαθεσιμότητας (tier + κλειστές μέρες) όταν επιλεγεί
  // παραλαβή από το κατάστημα ή παράδοση MrPanda — και τα δύο χρειάζονται
  // χρονοπρογραμματισμό.
  useEffect(() => {
    const needsSchedule =
      shippingMethod === "store_pickup" || shippingMethod === "mrpanda_delivery";
    if (!needsSchedule || items.length === 0) {
      setPickupInfo(null);
      setPickupSlot(null);
      setPickupError(false);
      setDeliverySlot(null);
      setDeliveryError(false);
      return;
    }
    let cancelled = false;
    const productIds = Array.from(new Set(items.map((it) => it.productId)));
    fetchPickupInfo({ data: { productIds } })
      .then((info) => {
        if (!cancelled) setPickupInfo(info);
      })
      .catch(() => {
        if (!cancelled) setPickupInfo(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingMethod, items]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    email: user?.email ?? "",
    fullName: "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    region: "",
    postalCode: "",
    country: "Ελλάδα",
    notes: "",
  });

  // Guest auth panel state
  const [authOpen, setAuthOpen] = useState(false);
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [showAuthPw, setShowAuthPw] = useState(false);
  const [authErr, setAuthErr] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(false);

  // Saved addresses pulled from the customer's previous orders
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [selectedAddressIdx, setSelectedAddressIdx] = useState<number | null>(null);

  // Keep email/name/phone in sync when the user logs in (inline or otherwise).
  // Name & phone are pulled from the account profile (user_metadata) and only
  // fill empty fields so we never overwrite something the user already typed.
  useEffect(() => {
    if (!user) return;
    const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
    const metaFullName =
      (typeof meta.full_name === "string" && meta.full_name.trim()) ||
      [meta.first_name ?? meta.firstname, meta.last_name ?? meta.lastname]
        .filter((p): p is string => typeof p === "string" && p.trim().length > 0)
        .join(" ")
        .trim();
    const metaPhone =
      typeof meta.phone === "string" ? normalizePhoneDigits(meta.phone) : "";
    setForm((f) => ({
      ...f,
      email: f.email || (user.email ?? ""),
      fullName: f.fullName || metaFullName || "",
      phone: f.phone || metaPhone,
    }));
  }, [user]);

  // Load the customer's saved addresses (from previous orders) once logged in.
  useEffect(() => {
    if (!user) {
      setSavedAddresses([]);
      setSelectedAddressIdx(null);
      return;
    }
    let cancelled = false;
    // Confirm a live session token exists before hitting the auth-protected
    // server fn. `user` can be set a tick before the bearer is attachable,
    // which would otherwise 401 with "No authorization header provided".
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (cancelled || !data.session?.access_token) return null;
        return fetchMyAddresses();
      })
      .then((res) => {
        if (!res) return;
        if (cancelled) return;
        const list = res.addresses ?? [];
        setSavedAddresses(list);
        // Imported accounts keep the phone (and sometimes the name) only inside
        // their previous orders, not in the auth metadata. Backfill any empty
        // contact fields from the customer profile first, then the most recent
        // saved address.
        const withPhone = res.contact?.phone || (list.find((a) => a.phone)?.phone ?? "");
        const withName = res.contact?.fullName || (list.find((a) => a.fullName)?.fullName ?? "");
        setForm((f) => ({
          ...f,
          phone: f.phone || withPhone,
          fullName: f.fullName || withName,
        }));
      })
      .catch(() => {
        if (!cancelled) setSavedAddresses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  function applySavedAddress(idx: number) {
    const a = savedAddresses[idx];
    if (!a) return;
    setSelectedAddressIdx(idx);
    setForm((f) => ({
      ...f,
      line1: a.line1,
      line2: a.line2,
      city: a.city,
      postalCode: a.postalCode,
      fullName: f.fullName || a.fullName,
      phone: f.phone || a.phone,
    }));
    setFieldErrors((e) => ({ ...e, line1: "", city: "", postalCode: "" }));
  }

  // When a full 5-digit postal code is entered and the city is still empty,
  // auto-fill the matching city via Google Maps geocoding.
  useEffect(() => {
    if (!/^\d{5}$/.test(form.postalCode.trim())) return;
    if (form.city.trim().length > 0) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await fetchCityByPostal({ data: { postalCode: form.postalCode.trim() } });
        if (cancelled) return;
        const city = res.cities[0];
        if (city) {
          setForm((f) => (f.city.trim().length === 0 ? { ...f, city } : f));
          setFieldErrors((e) => (e.city ? { ...e, city: "" } : e));
        }
      } catch {
        /* ignore — user can type the city manually */
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form.postalCode, form.city]);

  // Όταν συμπληρωθεί έγκυρος ελληνικός Τ.Κ. και η χώρα είναι Ελλάδα,
  // συμπλήρωσε αυτόματα τον νομό (αν δεν έχει επιλεγεί ήδη).
  useEffect(() => {
    if (!/^\d{5}$/.test(form.postalCode.trim())) return;
    if (form.country.trim() !== "Ελλάδα") return;
    if (form.region.trim().length > 0) return;
    const region = regionForGreekPostalCode(form.postalCode.trim());
    if (region) setForm((f) => (f.region.trim().length === 0 ? { ...f, region } : f));
  }, [form.postalCode, form.country, form.region]);

  // Store pickup is only available in the Thessaloniki prefecture (νομός)
  const isThessaloniki = /θεσσαλον/i.test(
    form.region
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, ""),
  );
  const availableShippingMethods = SHIPPING_METHODS;

  // Εύθραυστα προϊόντα στο καλάθι: δεν αποστέλλονται με courier. Επιτρέπονται
  // μόνο παραλαβή από το κατάστημα και παράδοση MrPanda εντός Θεσσαλονίκης.
  const hasFragileItem = items.some((it) => fragile.isFragile(it.psId, null));
  const FRAGILE_METHODS: ShippingMethod[] = ["store_pickup", "mrpanda_delivery"];

  // Cyprus is served only by Courier Center. When the customer types a Cyprus
  // country, restrict the options to Courier Center only.
  const isCyprus = /κυπρ|cyprus/.test(
    form.country
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, ""),
  );
  const visibleShippingMethods = isCyprus
    ? availableShippingMethods.filter((s) => s.id === "courier_center")
    : availableShippingMethods;
  const shownShippingMethods = hasFragileItem
    ? availableShippingMethods.filter((s) => FRAGILE_METHODS.includes(s.id))
    : visibleShippingMethods;

  // If a Greece-only method was selected but the destination is Cyprus, fall
  // back to Courier Center (the only courier serving Cyprus).
  useEffect(() => {
    if (hasFragileItem) return; // fragile guard has priority
    if (isCyprus && shippingMethod !== "courier_center") {
      setShippingMethod("courier_center");
    }
  }, [isCyprus, shippingMethod, hasFragileItem]);

  // Η παραλαβή από το κατάστημα είναι πλέον διαθέσιμη από παντού. Αν ο πελάτης
  // είναι εκτός Θεσσαλονίκης, ανοίγει ενημερωτικό pop-up με χάρτη & στοιχεία.
  useEffect(() => {
    if (maxStep < 5 || activeStep < 5) return;
    if (shippingMethod === "store_pickup" && !isThessaloniki) {
      setPickupInfoOpen(true);
    }
  }, [isThessaloniki, shippingMethod, maxStep, activeStep]);

  // Εύθραυστα: αν έχει επιλεγεί μη επιτρεπτός τρόπος, γύρνα σε παραλαβή.
  useEffect(() => {
    if (hasFragileItem && !FRAGILE_METHODS.includes(shippingMethod)) {
      setShippingMethod("store_pickup");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFragileItem, shippingMethod]);

  // MrPanda delivery: compute cost by driving distance from the store
  const isMrPanda = shippingMethod === "mrpanda_delivery";
  const isElta = shippingMethod === "elta_courier";
  const isAcs = shippingMethod === "acs";
  const isBoxnow = shippingMethod === "boxnow";
  const isCourierCenter = shippingMethod === "courier_center";
  const isSkroutz = shippingMethod === "skroutz_last_mile";
  const isCustomerFreight = shippingMethod === "customer_freight";

  // Σταθερό τηλέφωνο: τα locker courier (BoxNow / Skroutz Last Mile) απαιτούν
  // κινητό για το SMS του κωδικού παραλαβής, οπότε τα κλειδώνουμε.
  const phoneIsLandline = isLandlinePhone(form.phone);
  useEffect(() => {
    if (!phoneIsLandline) return;
    setLandlineNoticeOpen(true);
    setShippingMethod((m) => (LOCKER_METHODS.includes(m) ? "acs" : m));
  }, [phoneIsLandline]);

  const addrKey = `${form.line1}|${form.postalCode}|${form.city}`;
  useEffect(() => {
    if (
      form.line1.trim().length < 2 ||
      form.city.trim().length < 2 ||
      form.postalCode.trim().length < 3
    ) {
      setMrpanda(null);
      return;
    }
    let cancelled = false;
    setMrpandaLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await computeShipping({
          data: {
            line1: form.line1,
            line2: form.line2,
            city: form.city,
            postalCode: form.postalCode,
            subtotal,
          },
        });
        if (!cancelled) setMrpanda(res);
      } catch {
        if (!cancelled)
          setMrpanda({
            serviceable: false,
            distanceKm: null,
            cost: 0,
            freeOver: null,
            reason: "Σφάλμα υπολογισμού απόστασης.",
          });
      } finally {
        if (!cancelled) setMrpandaLoading(false);
      }
    }, 700);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [addrKey, subtotal]);

  // ΕΛΤΑ Courier: cost depends on total cart weight + whether Thessaloniki
  const itemsKey = items.map((i) => `${i.productId}x${i.quantity}`).join(",");
  useEffect(() => {
    if (items.length === 0) {
      return;
    }
    let cancelled = false;
    setEltaLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await computeElta({
          data: {
            city: form.city.trim() || "—",
            items: items.map((i) => ({ productId: i.productId, quantity: i.quantity })),
          },
        });
        if (!cancelled) setElta(res);
      } catch {
        if (!cancelled) setElta(null);
      } finally {
        if (!cancelled) setEltaLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [itemsKey, isThessaloniki, form.city]);

  // ACS Courier: weight + destination-zone based pricing
  useEffect(() => {
    if (items.length === 0 || form.postalCode.trim().length < 5) return;
    let cancelled = false;
    setAcsLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await computeAcs({
          data: {
            postalCode: form.postalCode.trim() || "00000",
            items: items.map((i) => ({ productId: i.productId, quantity: i.quantity })),
          },
        });
        if (!cancelled) setAcs(res);
      } catch {
        if (!cancelled) setAcs(null);
      } finally {
        if (!cancelled) setAcsLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [itemsKey, form.postalCode]);

  // Courier Center: weight + destination-zone based pricing
  useEffect(() => {
    if (items.length === 0 || form.postalCode.trim().length < 5) return;
    let cancelled = false;
    setCourierCenterLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await computeCourierCenter({
          data: {
            postalCode: form.postalCode.trim() || "00000",
            country: form.country.trim(),
            items: items.map((i) => ({ productId: i.productId, quantity: i.quantity })),
          },
        });
        if (!cancelled) setCourierCenter(res);
      } catch {
        if (!cancelled) setCourierCenter(null);
      } finally {
        if (!cancelled) setCourierCenterLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [itemsKey, form.postalCode, form.country]);

  // BoxNow: parcel-based pricing (1 δέμα ανά 15kg)
  useEffect(() => {
    if (items.length === 0 || form.postalCode.trim().length < 5) {
      return;
    }
    let cancelled = false;
    setBoxnowLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await computeBoxnow({
          data: {
            postalCode: form.postalCode.trim() || undefined,
            items: items.map((i) => ({ productId: i.productId, quantity: i.quantity })),
          },
        });
        if (!cancelled) setBoxnow(res);
      } catch {
        if (!cancelled) setBoxnow(null);
      } finally {
        if (!cancelled) setBoxnowLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [itemsKey, form.postalCode]);

  // Clear chosen locker when BoxNow is no longer selected
  useEffect(() => {
    if (!isBoxnow && locker) setLocker(null);
  }, [isBoxnow, locker]);

  // Skroutz Last Mile: 2,90€ (με ΦΠΑ) + χρέωση ζώνης (+ΦΠΑ) ανάλογα με τον ΤΚ
  // της επιλεγμένης θυρίδας. 1 δέμα έως 15kg ανά παραγγελία.
  useEffect(() => {
    if (items.length === 0) {
      return;
    }
    let cancelled = false;
    setSkroutzLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await computeSkroutz({
          data: {
            items: items.map((i) => ({ productId: i.productId, quantity: i.quantity })),
            postalCode: skroutzLocker?.postcode ?? undefined,
          },
        });
        if (!cancelled) setSkroutz(res);
      } catch {
        if (!cancelled) setSkroutz(null);
      } finally {
        if (!cancelled) setSkroutzLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [itemsKey, skroutzLocker?.postcode]);

  // Clear chosen Skroutz locker when no longer selected or when the parcel
  // is too heavy / large to fit in a Skroutz locker.
  useEffect(() => {
    if ((!isSkroutz || (skroutz && !skroutz.serviceable)) && skroutzLocker) {
      setSkroutzLocker(null);
    }
  }, [isSkroutz, skroutz, skroutzLocker]);

  // Reset the locker-dimensions acceptance if the customer switches away from
  // Skroutz Last Mile.
  useEffect(() => {
    if (!isSkroutz) {
      setSkroutzNoticeAccepted(false);
      setSkroutzNoticeOpen(false);
    }
  }, [isSkroutz]);

  // Store pickup has no shipping charge
  const isPickup = shippingMethod === "store_pickup";

  // Cash payment is only allowed for MrPanda delivery and store pickup.
  const cashAllowed = isMrPanda || isPickup;
  // Τα μεταφορικά εμφανίζονται μόνο αφού ο πελάτης ολοκληρώσει το βήμα αποστολής.
  const shippingChosen = maxStep >= 6;
  const availablePaymentMethods = METHODS.filter((m) => m.id !== "cash" || cashAllowed);
  useEffect(() => {
    if (!cashAllowed && method === "cash") setMethod("viva");
  }, [cashAllowed, method]);
  const effectiveShipping = isPickup
    ? 0
    : isMrPanda
      ? mrpanda?.serviceable
        ? mrpanda.cost
        : shipping
      : isElta
        ? (elta?.cost ?? shipping)
        : isAcs
          ? (acs?.cost ?? shipping)
          : isCourierCenter
            ? (courierCenter?.cost ?? shipping)
            : isBoxnow
              ? boxnow?.serviceable
                ? boxnow.cost
                : shipping
              : isSkroutz
                ? skroutz?.serviceable
                  ? skroutz.cost
                  : shipping
                : shipping;
  // Coupons never apply to products that are already on sale. The eligible base
  // for any coupon discount is the subtotal of the NON-discounted items only.
  const eligibleSubtotal = items.reduce(
    (s, it) =>
      s +
      (it.originalPrice != null && it.originalPrice > it.price
        ? 0
        : it.price * it.quantity),
    0,
  );
  const couponDiscount =
    coupon && coupon.discountAmount > 0
      ? Math.min(coupon.discountAmount, eligibleSubtotal)
      : 0;
  const bundleDiscount = computeCartBundleDiscount(items);
  const islandVatDiscount =
    docType === "invoice" && islandVat?.eligible ? islandVat.vatDiscount : 0;
  const effectiveTotal =
    Math.max(0, subtotal - couponDiscount - bundleDiscount - islandVatDiscount) +
    effectiveShipping;

  // Ανάλυση ΦΠΑ (μόνο για τιμολόγιο): οι τιμές του καταστήματος είναι με ΦΠΑ,
  // οπότε υπολογίζουμε την καθαρή αξία ανά γραμμή με τον συντελεστή του είδους.
  const netOf = (gross: number, rate: number) => gross / (1 + rate / 100);
  const itemRate = (it: (typeof items)[number]) =>
    Number((it as { taxRate?: number | null }).taxRate ?? 24) || 24;
  const subtotalNet = items.reduce(
    (s, it) => s + netOf(it.price * it.quantity, itemRate(it)),
    0,
  );
  const discountsTotal = couponDiscount + bundleDiscount + islandVatDiscount;
  // Οι εκπτώσεις κατανέμονται αναλογικά στους συντελεστές του καλαθιού.
  const discountsNet = subtotal > 0 ? discountsTotal * (subtotalNet / subtotal) : 0;
  const shippingNet = netOf(effectiveShipping, 24);
  const totalNet = Math.max(0, subtotalNet - discountsNet) + shippingNet;
  const vatAmount = Math.max(0, effectiveTotal - totalNet);


  // Live μειωμένος ΦΠΑ νησιών: όταν ζητηθεί τιμολόγιο και ο Τ.Κ. έδρας (από την
  // ΑΑΔΕ) ανήκει σε ζώνη μειωμένου ΦΠΑ, ζητάμε authoritative επανυπολογισμό.
  const invoiceVatEligible =
    docType === "invoice" &&
    /^\d{9}$/.test(vatNumber.trim()) &&
    !!islandForPostalCode(form.postalCode) &&
    !!islandForPostalCode(aadePostalCode);
  const islandItemsKey = items.map((i) => `${i.productId}x${i.quantity}`).join(",");
  useEffect(() => {
    if (!invoiceVatEligible || items.length === 0) {
      setIslandVat(null);
      return;
    }
    let cancelled = false;
    fetchIslandVat({
      data: {
        postalCode: form.postalCode,
        seatPostalCode: aadePostalCode,
        items: items.map((it) => ({ productId: it.productId, quantity: it.quantity })),
      },
    })
      .then((q) => {
        if (!cancelled) setIslandVat(q);
      })
      .catch(() => {
        if (!cancelled) setIslandVat(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceVatEligible, form.postalCode, aadePostalCode, islandItemsKey]);

  async function applyCoupon(override?: string) {
    const code = (override ?? couponInput).trim();
    if (!code || couponLoading) return;
    if (eligibleSubtotal <= 0) {
      setCoupon(null);
      setCouponMsg({
        type: "error",
        text: "Τα κουπόνια ισχύουν μόνο για προϊόντα σε κανονική τιμή — όλα τα προϊόντα του καλαθιού σου είναι ήδη σε προσφορά, οπότε έχεις ήδη την καλύτερη τιμή! 🎉",
      });
      return;
    }
    setCouponLoading(true);
    setCouponMsg(null);
    try {
      const res = await validateCouponFn({
        data: {
          code,
          cartTotal: eligibleSubtotal,
          email: form.email.trim() || undefined,
        },
      });
      if (res.valid) {
        setCoupon({ code: res.code, discountAmount: res.discountAmount });
        setCouponMsg({
          type: "ok",
          text: `Το κουπόνι «${res.code}» εφαρμόστηκε! 🎉`,
        });
        try {
          window.localStorage.removeItem(PENDING_COUPON_KEY);
        } catch {
          /* ignore */
        }
      } else {
        setCoupon(null);
        const text =
          res.error === "min_cart_not_met"
            ? res.minCartValue
              ? `Το κουπόνι ισχύει για καλάθι από ${formatPrice(res.minCartValue)} και πάνω. Πρόσθεσε ακόμη ${formatPrice(Math.max(0, res.minCartValue - eligibleSubtotal))} σε προϊόντα εκτός προσφοράς για να το χρησιμοποιήσεις.`
              : "Το καλάθι σου δεν φτάνει την ελάχιστη αξία για αυτό το κουπόνι."
            : res.error === "exhausted"
              ? "Αυτό το κουπόνι έχει ήδη χρησιμοποιηθεί σε προηγούμενη παραγγελία. Κάθε κουπόνι μπορεί να εξαργυρωθεί μία μόνο φορά."
              : res.error === "expired"
                ? res.expiresAt
                  ? `Το κουπόνι έληξε στις ${new Date(res.expiresAt).toLocaleDateString("el-GR")}. Τα κουπόνια ισχύουν για περιορισμένο διάστημα από την έκδοσή τους.`
                  : "Το κουπόνι έχει λήξει."
                 : res.error === "inactive"
                  ? "Αυτό το κουπόνι δεν είναι πλέον ενεργό. Αν πιστεύεις ότι είναι λάθος, επικοινώνησε μαζί μας."
                  : res.error === "new_customer_only"
                    ? "Το κουπόνι ισχύει μόνο για την πρώτη σου παραγγελία. Συμπλήρωσε το email σου για να το ελέγξουμε."
                    : res.error === "unavailable"
                      ? "Δεν μπορέσαμε να ελέγξουμε το κουπόνι αυτή τη στιγμή. Δοκίμασε ξανά σε λίγο."
                      : "Ο κωδικός κουπονιού δεν βρέθηκε. Έλεγξε ότι τον έγραψες σωστά, χωρίς κενά.";
        setCouponMsg({ type: "error", text });
      }
    } catch {
      setCoupon(null);
      setCouponMsg({ type: "error", text: "Κάτι πήγε στραβά κατά τον έλεγχο του κουπονιού." });
    } finally {
      setCouponLoading(false);
    }
  }

  function removeCoupon() {
    setCoupon(null);
    setCouponInput("");
    setCouponMsg(null);
    try {
      window.localStorage.removeItem(PENDING_COUPON_KEY);
    } catch {
      /* ignore */
    }
  }

  // Auto-apply κουπονιού που ήρθε από URL (?coupon=CODE) και αποθηκεύτηκε στο
  // localStorage. Τρέχει μία φορά μόλις το καλάθι έχει επιλέξιμο υποσύνολο.
  const autoAppliedRef = useRef(false);
  useEffect(() => {
    if (autoAppliedRef.current) return;
    if (coupon) {
      autoAppliedRef.current = true;
      return;
    }
    if (eligibleSubtotal <= 0) return;
    let pending: string | null = null;
    try {
      pending = window.localStorage.getItem(PENDING_COUPON_KEY);
    } catch {
      /* ignore */
    }
    if (pending && pending.trim()) {
      autoAppliedRef.current = true;
      const code = pending.trim().toUpperCase();
      setCouponInput(code);
      void applyCoupon(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleSubtotal, coupon]);

  // Re-validate an applied coupon whenever the cart subtotal changes so the
  // displayed discount stays in sync with the (possibly changed) cart.
  useEffect(() => {
    if (!coupon) return;
    let cancelled = false;
    if (eligibleSubtotal <= 0) {
      setCoupon(null);
      setCouponMsg({
        type: "error",
        text: "Το κουπόνι δεν μπορεί να χρησιμοποιηθεί: όλα τα προϊόντα του καλαθιού είναι ήδη σε προσφορά.",
      });
      return;
    }
    validateCouponFn({ data: { code: coupon.code, cartTotal: eligibleSubtotal } })
      .then((res) => {
        if (cancelled) return;
        if (res.valid) setCoupon({ code: res.code, discountAmount: res.discountAmount });
        else {
          setCoupon(null);
          setCouponMsg({
            type: "error",
            text:
              res.error === "min_cart_not_met" && res.minCartValue
                ? `Το κουπόνι αφαιρέθηκε: ισχύει για καλάθι από ${formatPrice(res.minCartValue)} και πάνω, ενώ τα προϊόντα εκτός προσφοράς στο καλάθι σου είναι τώρα ${formatPrice(eligibleSubtotal)}.`
                : "Το κουπόνι αφαιρέθηκε γιατί δεν ισχύει πλέον για το τρέχον καλάθι.",
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleSubtotal]);

  // Per-courier cost badge shown in the shipping method list, so the customer
  // sees every price at once without selecting each option one by one.
  const renderMethodCost = (id: ShippingMethod) => {
    const free = (
      <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">
        Δωρεάν
      </span>
    );
    const price = (v: number) =>
      v === 0 ? (
        free
      ) : (
        <span className="shrink-0 text-sm font-bold text-foreground">{formatPrice(v)}</span>
      );
    const loadingBadge = <span className="shrink-0 text-xs text-muted-foreground">…</span>;
    const unavailable = (
      <span className="shrink-0 text-xs font-medium text-muted-foreground">Μη διαθέσιμο</span>
    );
    switch (id) {
      case "store_pickup":
        return free;
      case "mrpanda_delivery":
        return mrpandaLoading
          ? loadingBadge
          : mrpanda
            ? mrpanda.serviceable
              ? price(mrpanda.cost)
              : unavailable
            : null;
      case "elta_courier":
        return eltaLoading ? loadingBadge : elta ? price(elta.cost) : null;
      case "acs":
        return acsLoading ? loadingBadge : acs ? price(acs.cost) : null;
      case "courier_center":
        return courierCenterLoading
          ? loadingBadge
          : courierCenter
            ? price(courierCenter.cost)
            : null;
      case "boxnow":
        return boxnowLoading
          ? loadingBadge
          : boxnow
            ? boxnow.serviceable
              ? price(boxnow.cost)
              : unavailable
            : null;
      case "skroutz_last_mile":
        return skroutzLoading
          ? loadingBadge
          : skroutz
            ? skroutz.serviceable
              ? price(skroutz.cost)
              : unavailable
            : null;
      case "customer_freight":
        return (
          <span className="shrink-0 text-xs font-medium text-muted-foreground">Χρέωση πελάτη</span>
        );
      default:
        return null;
    }
  };

  // Progressive disclosure: each step appears once the previous one is complete.
  const contactComplete =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) &&
    isValidPhone(form.phone) &&
    form.fullName.trim().length >= 2;
  const docComplete = docType === "receipt" || /^\d{9}$/.test(vatNumber.trim());
  const addressComplete =
    form.line1.trim().length >= 1 &&
    form.city.trim().length >= 1 &&
    /^\d{5}$/.test(form.postalCode.trim());
  const oosComplete = !!outOfStockAction;
  const shippingComplete =
    addressComplete &&
    oosComplete &&
    (!isBoxnow || !!locker) &&
    (!isSkroutz || !!skroutzLocker) &&
    (!isCustomerFreight ||
      freightNeedsSuggestion ||
      (freightConfirmed && freightCarrier.trim().length >= 3));

  function update<K extends keyof typeof form>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
    if (
      k === "line1" ||
      k === "line2" ||
      k === "city" ||
      k === "region" ||
      k === "postalCode" ||
      k === "country"
    ) {
      setSelectedAddressIdx(null);
    }
    setFieldErrors((e) => (e[k] ? { ...e, [k]: "" } : e));
  }

  async function fetchFromAade() {
    if (!/^\d{9}$/.test(vatNumber.trim())) {
      setAadeMsg({ type: "error", text: "Συμπλήρωσε ένα έγκυρο 9ψήφιο ΑΦΜ." });
      return;
    }
    setAadeLoading(true);
    setAadeMsg(null);
    try {
      const res = await fetchVatDetails({ data: { vatNumber: vatNumber.trim() } });
      if (!res.ok) {
        setAadeMsg({ type: "error", text: res.error });
        return;
      }
      const b = res.business;
      setCompanyName(b.name);
      setCompanyProfession(b.profession);
      setCompanyDoy(b.doy);
      setAadePostalCode(b.postalCode || "");
      const line1 = [b.street, b.streetNo].filter(Boolean).join(" ").trim();
      setForm((f) => ({
        ...f,
        line1: line1 || f.line1,
        city: b.city || f.city,
        postalCode: b.postalCode || f.postalCode,
        region: b.region || f.region,
      }));
      if (line1 || b.city || b.postalCode) setSelectedAddressIdx(null);
      setFieldErrors((e) => ({ ...e, vatNumber: "" }));
      setAadeMsg({
        type: "ok",
        text: b.inactive
          ? "Τα στοιχεία συμπληρώθηκαν, αλλά το ΑΦΜ φαίνεται σε διακοπή εργασιών."
          : "Τα στοιχεία συμπληρώθηκαν από την ΑΑΔΕ.",
      });
    } catch {
      setAadeMsg({
        type: "error",
        text: "Αποτυχία αναζήτησης. Δοκίμασε ξανά ή συμπλήρωσε χειροκίνητα.",
      });
    } finally {
      setAadeLoading(false);
    }
  }

  async function inlineLogin(e: React.FormEvent) {
    e.preventDefault();
    setAuthErr(null);
    setAuthLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: authEmail,
      password: authPassword,
    });
    if (!error) {
      setAuthLoading(false);
      setAuthOpen(false);
      return;
    }
    if (error.message === "Invalid login credentials") {
      try {
        const res = await migrate({ data: { email: authEmail, password: authPassword } });
        if (res.migrated) {
          const retry = await supabase.auth.signInWithPassword({
            email: authEmail,
            password: authPassword,
          });
          setAuthLoading(false);
          if (!retry.error) {
            setAuthOpen(false);
            return;
          }
          setAuthErr("Λάθος email ή κωδικός.");
          return;
        }
      } catch {
        // fall through
      }
      setAuthLoading(false);
      setAuthErr("Λάθος email ή κωδικός.");
      return;
    }
    setAuthLoading(false);
    setAuthErr(error.message);
  }

  function validate() {
    const fe: Record<string, string> = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      fe.email = "Συμπλήρωσε ένα έγκυρο email (π.χ. name@example.com).";
    if (!isValidPhone(form.phone))
      fe.phone = PHONE_ERROR;
    if (form.fullName.trim().length < 2) fe.fullName = "Συμπλήρωσε το ονοματεπώνυμό σου.";
    if (docType === "invoice" && !/^\d{9}$/.test(vatNumber.trim()))
      fe.vatNumber = "Το ΑΦΜ πρέπει να έχει ακριβώς 9 ψηφία.";
    if (form.line1.trim().length < 1) fe.line1 = "Συμπλήρωσε τη διεύθυνση.";
    if (form.city.trim().length < 1) fe.city = "Συμπλήρωσε την πόλη.";
    if (!/^\d{5}$/.test(form.postalCode.trim()))
      fe.postalCode = "Ο Τ.Κ. πρέπει να έχει ακριβώς 5 ψηφία.";
    if (!outOfStockAction)
      fe.outOfStockAction = "Επίλεξε τρόπο επικοινωνίας σε περίπτωση ελλείψεων.";
    return fe;
  }

  function stepErrors(step: number): Record<string, string> {
    const fe: Record<string, string> = {};
    if (step === 1) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
        fe.email = "Συμπλήρωσε ένα έγκυρο email (π.χ. name@example.com).";
      if (!isValidPhone(form.phone))
        fe.phone = PHONE_ERROR;
      if (form.fullName.trim().length < 2) fe.fullName = "Συμπλήρωσε το ονοματεπώνυμό σου.";
    } else if (step === 2) {
      if (docType === "invoice" && !/^\d{9}$/.test(vatNumber.trim()))
        fe.vatNumber = "Το ΑΦΜ πρέπει να έχει ακριβώς 9 ψηφία.";
    } else if (step === 3) {
      if (form.line1.trim().length < 1) fe.line1 = "Συμπλήρωσε τη διεύθυνση.";
      if (form.city.trim().length < 1) fe.city = "Συμπλήρωσε την πόλη.";
      if (!/^\d{5}$/.test(form.postalCode.trim()))
        fe.postalCode = "Ο Τ.Κ. πρέπει να έχει ακριβώς 5 ψηφία.";
    } else if (step === 4) {
      if (!outOfStockAction)
        fe.outOfStockAction = "Επίλεξε τρόπο επικοινωνίας σε περίπτωση ελλείψεων.";
    }
    return fe;
  }

  function shippingError(): string | null {
    if (isMrPanda) {
      if (mrpandaLoading) return "Περίμενε να ολοκληρωθεί ο υπολογισμός των μεταφορικών… ⏳";
      if (!mrpanda || !mrpanda.serviceable)
        return (
          mrpanda?.reason ??
          "Δεν μπορούμε να παραδώσουμε με Παράδοση MrPanda σε αυτή τη διεύθυνση. Διάλεξε άλλον τρόπο αποστολής."
        );
      if (pickupInfo && !deliverySlot)
        return "Διάλεξε παράθυρο παράδοσης για την παραγγελία σου. 🚐";
    }
    if (isBoxnow) {
      if (boxnowLoading) return "Περίμενε να ολοκληρωθεί ο υπολογισμός των δεμάτων… ⏳";
      if (boxnow && !boxnow.serviceable)
        return boxnow.reason ?? "Η παραγγελία δεν μπορεί να σταλεί με BoxNow.";
      if (!locker) return "Διάλεξε θυρίδα BoxNow για να συνεχίσεις. 📦";
    }
    if (isSkroutz) {
      if (skroutzLoading) return "Περίμενε να ολοκληρωθεί ο υπολογισμός των μεταφορικών… ⏳";
      if (skroutz && !skroutz.serviceable)
        return skroutz.reason ?? "Η παραγγελία δεν μπορεί να σταλεί με Skroutz Last Mile.";
      if (!skroutzLocker) return "Διάλεξε θυρίδα Skroutz Last Mile για να συνεχίσεις. 📦";
    }
    if (
      isCustomerFreight &&
      !freightNeedsSuggestion &&
      (!freightConfirmed || freightCarrier.trim().length < 3)
    )
      return "Γράψε τη μεταφορική εταιρεία (3+ χαρακτήρες) και πάτα «Αποθήκευση» ή ζήτησε πρόταση από το MrPanda. 🚚";
    if (isPickup && pickupInfo && !pickupSlot)
      return "Διάλεξε πότε θα παραλάβεις την παραγγελία σου από το κατάστημα. 🗓️";
    return null;
  }

  function goToStep(step: number) {
    setErr(null);
    setActiveStep(step);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // Συνδεδεμένος πελάτης με ήδη συμπληρωμένα στοιχεία επικοινωνίας: προχωράμε
  // αυτόματα στο βήμα 2 (τύπος παραστατικού) αντί να ξαναδεί το βήμα 1.
  const autoSkippedContact = useRef(false);
  useEffect(() => {
    if (autoSkippedContact.current) return;
    if (!user) return;
    if (activeStep !== 1) {
      autoSkippedContact.current = true;
      return;
    }
    if (Object.keys(stepErrors(1)).length > 0) return;
    autoSkippedContact.current = true;
    setActiveStep(2);
    setMaxStep((m) => Math.max(m, 2));
  }, [user, activeStep, form.email, form.phone, form.fullName]);

  function goNext(step: number) {
    setErr(null);
    const fe = stepErrors(step);
    if (Object.keys(fe).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...fe }));
      setErr("Έλεγξε τα πεδία με κόκκινο — κάτι λείπει 🐼");
      return;
    }
    if (step === 5) {
      const se = shippingError();
      if (se) {
        setErr(se);
        return;
      }
      // Skroutz Last Mile: the customer must accept the locker-size disclaimer
      // before moving on to the payment step.
      if (isSkroutz && !skroutzNoticeAccepted) {
        setSkroutzNoticeOpen(true);
        return;
      }
    }
    const next = step + 1;
    setActiveStep(next);
    setMaxStep((m) => Math.max(m, next));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (items.length === 0) return;
    const fe = validate();
    if (Object.keys(fe).length > 0) {
      setFieldErrors(fe);
      setErr("Έλεγξε τα πεδία με κόκκινο — κάτι λείπει 🐼");
      return;
    }
    if (isMrPanda) {
      if (mrpandaLoading) {
        setErr("Περίμενε να ολοκληρωθεί ο υπολογισμός των μεταφορικών… ⏳");
        return;
      }
      if (!mrpanda || !mrpanda.serviceable) {
        setErr(
          mrpanda?.reason ??
            "Δεν μπορούμε να παραδώσουμε με Παράδοση MrPanda σε αυτή τη διεύθυνση. Διάλεξε άλλον τρόπο αποστολής.",
        );
        return;
      }
      if (pickupInfo && !deliverySlot) {
        setDeliveryError(true);
        setErr("Διάλεξε παράθυρο παράδοσης για την παραγγελία σου. 🚐");
        return;
      }
    }
    if (isBoxnow) {
      if (boxnowLoading) {
        setErr("Περίμενε να ολοκληρωθεί ο υπολογισμός των δεμάτων… ⏳");
        return;
      }
      if (boxnow && !boxnow.serviceable) {
        setErr(boxnow.reason ?? "Η παραγγελία δεν μπορεί να σταλεί με BoxNow.");
        return;
      }
      if (!locker) {
        setErr("Διάλεξε θυρίδα BoxNow για να συνεχίσεις. 📦");
        return;
      }
    }
    if (isSkroutz) {
      if (skroutzLoading) {
        setErr("Περίμενε να ολοκληρωθεί ο υπολογισμός των μεταφορικών… ⏳");
        return;
      }
      if (skroutz && !skroutz.serviceable) {
        setErr(skroutz.reason ?? "Η παραγγελία δεν μπορεί να σταλεί με Skroutz Last Mile.");
        return;
      }
      if (!skroutzLocker) {
        setErr("Διάλεξε θυρίδα Skroutz Last Mile για να συνεχίσεις. 📦");
        return;
      }
    }
    if (
      isCustomerFreight &&
      !freightNeedsSuggestion &&
      (!freightConfirmed || freightCarrier.trim().length < 3)
    ) {
      setErr(
        "Γράψε τη μεταφορική εταιρεία (3+ χαρακτήρες) και πάτα «Αποθήκευση» ή ζήτησε πρόταση από το MrPanda. 🚚",
      );
      return;
    }
    if (isPickup) {
      if (pickupInfo && !pickupSlot) {
        setPickupError(true);
        setErr("Διάλεξε πότε θα παραλάβεις την παραγγελία σου από το κατάστημα. 🗓️");
        return;
      }
    }
    if (method === "bank_transfer" && !selectedBank) {
      setErr("Διάλεξε σε ποια τράπεζα θα κάνεις την κατάθεση για να συνεχίσεις. 🏦");
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const shippingNote =
        isBoxnow && locker
          ? `BoxNow θυρίδα: ${locker.name ?? locker.id}${locker.address ? ` — ${locker.address}` : ""}${locker.postcode ? `, ${locker.postcode}` : ""} (ID: ${locker.id})`
          : isSkroutz && skroutzLocker
            ? `Skroutz Last Mile θυρίδα: ${skroutzLocker.name ?? skroutzLocker.id}${skroutzLocker.address ? ` — ${skroutzLocker.address}` : ""}${skroutzLocker.postcode ? `, ${skroutzLocker.postcode}` : ""} (ID: ${skroutzLocker.id})`
            : isCustomerFreight && freightNeedsSuggestion
              ? "Μεταφορική πελάτη: Ο πελάτης δεν έχει συνεργαζόμενη μεταφορική — ζητά πρόταση από το MrPanda"
              : isCustomerFreight && freightCarrier.trim()
                ? `Μεταφορική πελάτη: ${freightCarrier.trim()}`
                : "";
      const bankNote =
        method === "bank_transfer" && selectedBank
          ? `Κατάθεση στον λογαριασμό: ${selectedBank}`
          : "";
      const docNote =
        docType === "invoice"
          ? [
              "Τύπος παραστατικού: Τιμολόγιο πώλησης",
              companyName.trim() && `Επωνυμία: ${companyName.trim()}`,
              companyDoy.trim() && `ΔΟΥ: ${companyDoy.trim()}`,
              companyProfession.trim() && `Δραστηριότητα: ${companyProfession.trim()}`,
            ]
              .filter(Boolean)
              .join("\n")
          : "Τύπος παραστατικού: Απόδειξη λιανικής πώλησης";
      const oosLabel = OUT_OF_STOCK_OPTIONS.find((o) => o.id === outOfStockAction)?.label ?? "";
      const oosNote = oosLabel ? `Επικοινωνία σε περίπτωση ελλείψεων: ${oosLabel}` : "";
      const courierNote =
        (isAcs || isElta || isCourierCenter) && courierNotes.trim()
          ? `Σχόλια courier: ${courierNotes.trim()}`
          : "";
      const pickupNote =
        isPickup && pickupSlot
          ? [
              `Παραλαβή από κατάστημα: ${pickupSlot.label}`,
              pickupInfo?.hasPreorder && "Περιέχει προϊόν κατόπιν παραγγελίας",
              "Η παραγγελία παραμένει στο κατάστημα έως 5 εργάσιμες ημέρες από το επιλεγμένο παράθυρο",
              "Ο πελάτης ενημερώθηκε ότι θα λάβει SMS όταν η παραγγελία είναι έτοιμη και να μην προσέλθει πριν την ειδοποίηση",
            ]
              .filter(Boolean)
              .join("\n")
          : "";
      const deliveryNote =
        isMrPanda && deliverySlot
          ? [
              `Παράθυρο παράδοσης MrPanda: ${deliverySlot.label}`,
              mrpanda?.distanceKm != null &&
                `Απόσταση: ${mrpanda.distanceKm} χλμ (${mrpanda.distanceKm >= 10 ? "πάνω από 10 χλμ" : "έως 10 χλμ"})`,
              pickupInfo?.hasPreorder && "Περιέχει προϊόν κατόπιν παραγγελίας",
            ]
              .filter(Boolean)
              .join("\n")
          : "";
      const composedNotes = [
        form.notes,
        docNote,
        oosNote,
        shippingNote,
        courierNote,
        pickupNote,
        deliveryNote,
        bankNote,
      ]
        .filter(Boolean)
        .join("\n");
      const validatedNotes = orderNotesSchema.safeParse(composedNotes);
      if (!validatedNotes.success) {
        setErr(validatedNotes.error.issues[0]?.message ?? "Συντόμευσε τις σημειώσεις της παραγγελίας και δοκίμασε ξανά.");
        setLoading(false);
        return;
      }
      const doneTiming = startTiming("checkout:place-order");
      const res = await createOrderFn({
        data: {
          email: form.email,
          fullName: form.fullName,
          phone: form.phone,
          notes: validatedNotes.data,
          shippingAddress: {
            line1: form.line1,
            line2: form.line2,
            city: form.city,
            region: form.region.trim(),
            postalCode: form.postalCode,
            country: form.country.trim() || "Ελλάδα",
            vatNumber: docType === "invoice" ? vatNumber.trim() : "",
            vatPostalCode: docType === "invoice" ? aadePostalCode : "",
          },
          items: items.map((it) => ({
            productId: it.productId,
            name: it.name,
            price: it.price,
            quantity: it.quantity,
            imageUrl: it.imageUrl,
            bundleId: it.bundleId ?? null,
            bundleKey: it.bundleKey ?? null,
          })),
          paymentMethod: method,
          shippingMethod,
          shippingMethodLabel:
            SHIPPING_METHODS.find((s) => s.id === shippingMethod)?.label ?? shippingMethod,
          pickupPoint:
            isBoxnow && locker
              ? {
                  provider: "boxnow" as const,
                  id: locker.id,
                  name: locker.name ?? undefined,
                  address: locker.address ?? undefined,
                  postcode: locker.postcode ?? undefined,
                }
              : isSkroutz && skroutzLocker
                ? {
                    provider: "skroutz" as const,
                    id: skroutzLocker.id,
                    name: skroutzLocker.name ?? undefined,
                    address: skroutzLocker.address ?? undefined,
                    postcode: skroutzLocker.postcode ?? undefined,
                  }
                : undefined,
          documentType: docType,
          outOfStockAction: oosLabel || undefined,
          couponCode: coupon?.code || undefined,
          bundleIds: Array.from(
            new Set(items.map((it) => it.bundleId).filter((b): b is string => !!b)),
          ),
          invoice:
            docType === "invoice"
              ? {
                  company: companyName.trim(),
                  doy: companyDoy.trim(),
                  profession: companyProfession.trim(),
                }
              : undefined,
        },
      });
      doneTiming();
      if ("klarna" in res && res.klarna) {
        setKlarnaPayment({ guestToken: res.guestToken, ...res.klarna });
        setLoading(false);
        return;
      }
      localStorage.removeItem("mrpanda.cart.v1");
      setCoupon(null);
      localStorage.removeItem("mp_checkout_active_step");
      localStorage.removeItem("mp_checkout_max_step");
      if (res.redirectUrl) {
        window.location.href = res.redirectUrl;
        return;
      }
      navigate({ to: "/order/$token", params: { token: res.guestToken } });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Σφάλμα παραγγελίας");
      setLoading(false);
    }
  }

  if (items.length === 0) {
    return (
      <StorefrontLayout hideCartSidebar minimalHeader minimalFooter>
        <div className="mx-auto max-w-md px-4 py-16 text-center">
          <div className="mx-auto grid h-24 w-24 place-items-center rounded-full bg-accent text-6xl animate-bob">
            🐼
          </div>
          <h1 className="mt-4 text-2xl font-bold">Το καλάθι σου είναι άδειο</h1>
          <p className="mt-2 text-muted-foreground">
            Ο Mr Panda περιμένει να γεμίσεις το καλάθι σου με ωραία πραγματάκια! 🛍️
          </p>
          <Link
            to="/"
            className="mp-bouncy mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground hover:opacity-90"
          >
            Πάμε για ψώνια 🚀
          </Link>
        </div>
      </StorefrontLayout>
    );
  }

  return (
    <StorefrontLayout
      hideCartSidebar
      minimalHeader
      minimalFooter
      headerLeft={
        <Link
          to="/cart"
          className="mp-bouncy hover-wiggle group inline-flex items-center gap-1.5 rounded-full border-2 border-primary/30 bg-primary/10 px-2.5 py-1.5 text-xs font-semibold text-primary shadow-sm transition-all hover:border-primary/60 hover:bg-primary/15 hover:shadow-md sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          <span className="hidden sm:inline">Επιστροφή στο καλάθι</span>
          <span className="sm:hidden">Καλάθι</span>
          <span className="text-base transition-transform group-hover:-rotate-12" aria-hidden>
            🛒
          </span>
        </Link>
      }
    >
      <div className="mx-auto max-w-none px-4 pt-6 pb-12">
        <div className="relative mb-6 overflow-hidden rounded-3xl border border-primary/20 mp-playful-panel p-5 sm:p-6">
          <span
            className="pointer-events-none absolute right-4 top-3 text-2xl animate-float-y"
            style={{ animationDelay: "0.2s" }}
          >
            ✨
          </span>
          <span
            className="pointer-events-none absolute right-12 bottom-3 text-xl animate-float-y"
            style={{ animationDelay: "0.8s" }}
          >
            🎁
          </span>
          <span
            className="pointer-events-none absolute right-24 top-6 hidden text-lg animate-float-y sm:block"
            style={{ animationDelay: "1.4s" }}
          >
            🛍️
          </span>
          <div className="relative flex items-center gap-3">
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-primary to-amber-500 text-3xl shadow-md animate-bob hover-wiggle">
              🐼
            </div>
            <div>
              <h1 className="text-2xl font-bold sm:text-3xl">Ολοκλήρωση παραγγελίας</h1>
              <p className="text-sm text-muted-foreground">
                Λίγα βήματα ακόμη και τα πραγματάκια σου ξεκινούν ταξίδι! ✨
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          {klarnaPayment && (
            <KlarnaPaymentDialog open guestToken={klarnaPayment.guestToken}
              session={klarnaPayment}
              onCancel={() => setKlarnaPayment(null)}
              onSuccess={(token) => {
                clear(); setCoupon(null); setKlarnaPayment(null);
                localStorage.removeItem("mp_checkout_active_step");
                localStorage.removeItem("mp_checkout_max_step");
                void navigate({ to: "/order/$token", params: { token } });
              }} />
          )}
          <form id="checkout-form" onSubmit={submit} className="min-w-0 space-y-5">
            {/* Account / login panel */}
            {user ? (
              <div className="flex items-center gap-3 rounded-2xl border border-success/30 bg-success/10 p-4">
                <div className="grid h-10 w-10 place-items-center rounded-full bg-success/20 text-success">
                  <Check className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1 text-sm">
                  <div className="font-semibold">Συνδεδεμένος ως {user.email}</div>
                  <div className="text-muted-foreground">
                    Η παραγγελία θα αποθηκευτεί στον λογαριασμό σου. 🎉
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => supabase.auth.signOut()}
                  className="shrink-0 rounded px-3 py-2 text-sm text-destructive hover:bg-destructive/10"
                >
                  Αποσύνδεση
                </button>
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-primary/30 mp-playful-panel">
                <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent text-2xl">
                      🐼
                    </div>
                    <div className="text-sm">
                      <div className="font-semibold">Έχεις λογαριασμό;</div>
                      <div className="text-muted-foreground">
                        Συνδέσου για ταχύτερη ολοκλήρωση, ή συνέχισε ως επισκέπτης.
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAuthOpen((v) => !v)}
                    className="mp-bouncy inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-primary bg-primary/5 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10"
                  >
                    <LogIn className="h-4 w-4" /> {authOpen ? "Κλείσιμο" : "Σύνδεση"}
                  </button>
                </div>
                {authOpen && (
                  <div className="animate-fade-in border-t border-primary/20 bg-card/60 p-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="block text-sm">
                        <span className="mb-1 block text-muted-foreground">📧 Email</span>
                        <input
                          value={authEmail}
                          onChange={(e) => setAuthEmail(e.target.value)}
                          type="email"
                          className="h-10 w-full rounded-md border border-border bg-background px-3 outline-none focus:border-primary"
                        />
                      </label>
                      <label className="block text-sm">
                        <span className="mb-1 block text-muted-foreground">🔒 Κωδικός</span>
                        <div className="relative">
                          <input
                            value={authPassword}
                            onChange={(e) => setAuthPassword(e.target.value)}
                            type={showAuthPw ? "text" : "password"}
                            className="h-10 w-full rounded-md border border-border bg-background px-3 pr-10 outline-none focus:border-primary"
                          />
                          <button
                            type="button"
                            onClick={() => setShowAuthPw((v) => !v)}
                            aria-label={showAuthPw ? "Απόκρυψη κωδικού" : "Προβολή κωδικού"}
                            className="absolute inset-y-0 right-0 grid w-10 place-items-center text-muted-foreground hover:text-foreground"
                          >
                            {showAuthPw ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </label>
                    </div>
                    {authErr && (
                      <div className="mt-2 rounded-md bg-destructive/10 p-2 text-sm text-destructive">
                        {authErr}
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={inlineLogin}
                        disabled={authLoading}
                        className="mp-bouncy rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
                      >
                        {authLoading ? "Σύνδεση..." : "Σύνδεση 🚀"}
                      </button>
                      <Link to="/signup" className="text-sm text-primary hover:underline">
                        Δημιουργία λογαριασμού
                      </Link>
                      <Link
                        to="/reset-password"
                        className="text-sm text-muted-foreground hover:underline"
                      >
                        Ξέχασες τον κωδικό;
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}

            <Section
              icon={User}
              title="Στοιχεία επικοινωνίας"
              step={1}
              total={6}
              active={activeStep === 1}
              done={activeStep !== 1}
              onEdit={() => goToStep(1)}
              onNext={() => goNext(1)}
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label="Email"
                  required
                  value={form.email}
                  onChange={(v) => update("email", v)}
                  type="email"
                  error={fieldErrors.email}
                />
                <Field
                  label="Τηλέφωνο επικοινωνίας"
                  required
                  value={form.phone}
                  onChange={(v) => update("phone", normalizePhoneDigits(v))}
                  type="tel"
                  inputMode="numeric"
                  maxLength={12}
                  placeholder="π.χ. 6912345678 ή 99123456 (Κύπρος)"
                  error={fieldErrors.phone}
                />

                <Field
                  label="Ονοματεπώνυμο"
                  required
                  value={form.fullName}
                  onChange={(v) => update("fullName", v)}
                  className="sm:col-span-2"
                  error={fieldErrors.fullName}
                />
              </div>
              {!user && (
                <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                  🐼 Παραγγέλνεις ως επισκέπτης. Θα λάβεις σύνδεσμο παρακολούθησης στο email σου.
                </p>
              )}
            </Section>

            {maxStep >= 2 && (
              <Section
                icon={FileText}
                title="Τύπος παραστατικού"
                step={2}
                total={6}
                active={activeStep === 2}
                done={activeStep !== 2}
                onEdit={() => goToStep(2)}
                onNext={() => goNext(2)}
              >
                <div className="grid gap-2 sm:grid-cols-2">
                  {[
                    {
                      id: "receipt" as const,
                      label: "Απόδειξη λιανικής πώλησης",
                      desc: "Προεπιλογή για ιδιώτες",
                      icon: Receipt,
                    },
                    {
                      id: "invoice" as const,
                      label: "Τιμολόγιο πώλησης",
                      desc: "Για επιχειρήσεις — απαιτείται ΑΦΜ",
                      icon: FileText,
                    },
                  ].map((d) => {
                    const Icon = d.icon;
                    const active = docType === d.id;
                    return (
                      <label
                        key={d.id}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 p-3 transition ${active ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-primary/40"}`}
                      >
                        <input
                          type="radio"
                          name="doc-type"
                          checked={active}
                          onChange={() => setDocType(d.id)}
                          className="sr-only"
                        />
                        <div
                          className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold">{d.label}</div>
                          <div className="text-xs text-muted-foreground">{d.desc}</div>
                        </div>
                        {active && <Check className="h-5 w-5 shrink-0 text-primary" />}
                      </label>
                    );
                  })}
                </div>
                {docType === "invoice" && (
                  <div className="mt-3 space-y-3">
                    <div className="grid items-end gap-3 sm:grid-cols-[1fr_auto]">
                      <Field
                        label="ΑΦΜ πελάτη"
                        required
                        value={vatNumber}
                        onChange={(v) => {
                          setVatNumber(v.replace(/\D/g, "").slice(0, 9));
                          setAadeMsg(null);
                          // Ο Τ.Κ. έδρας ισχύει μόνο για το ΑΦΜ που ανακτήθηκε.
                          setAadePostalCode("");
                          setFieldErrors((e) => (e.vatNumber ? { ...e, vatNumber: "" } : e));
                        }}
                        inputMode="numeric"
                        maxLength={9}
                        placeholder="π.χ. 123456789"
                        error={fieldErrors.vatNumber}
                      />
                      <button
                        type="button"
                        onClick={fetchFromAade}
                        disabled={aadeLoading || !/^\d{9}$/.test(vatNumber.trim())}
                        className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
                      >
                        {aadeLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Search className="h-4 w-4" />
                        )}
                        Συμπλήρωση από ΑΑΔΕ
                      </button>
                    </div>
                    {aadeMsg && (
                      <p
                        className={`text-xs ${aadeMsg.type === "ok" ? "text-emerald-600" : "text-destructive"}`}
                      >
                        {aadeMsg.text}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Πάτησε «Συμπλήρωση από ΑΑΔΕ» για αυτόματη άντληση επωνυμίας, ΔΟΥ, επαγγέλματος
                      και διεύθυνσης. Μπορείς να τα επεξεργαστείς ελεύθερα.
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field
                        label="Επωνυμία"
                        value={companyName}
                        onChange={setCompanyName}
                        className="sm:col-span-2"
                        placeholder="Επωνυμία επιχείρησης"
                      />
                      <Field
                        label="ΔΟΥ"
                        value={companyDoy}
                        onChange={setCompanyDoy}
                        placeholder="π.χ. ΦΑΕ Αθηνών"
                      />
                      <Field
                        label="Επάγγελμα / Δραστηριότητα"
                        value={companyProfession}
                        onChange={setCompanyProfession}
                        placeholder="π.χ. Λιανικό εμπόριο"
                      />
                    </div>
                  </div>
                )}
              </Section>
            )}

            {maxStep >= 3 && (
              <Section
                icon={MapPin}
                title="Διεύθυνση αποστολής"
                step={3}
                total={6}
                active={activeStep === 3}
                done={activeStep !== 3}
                onEdit={() => goToStep(3)}
                onNext={() => goNext(3)}
              >
                {savedAddresses.length > 0 && (
                  <div className="mb-4">
                    <p className="mb-2 text-sm font-semibold">📍 Αποθηκευμένες διευθύνσεις</p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {savedAddresses.map((a, idx) => {
                        const active = selectedAddressIdx === idx;
                        return (
                          <button
                            type="button"
                            key={`${a.line1}|${a.postalCode}|${idx}`}
                            onClick={() => applySavedAddress(idx)}
                            className={`flex items-start gap-3 rounded-xl border-2 p-3 text-left transition ${active ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-primary/40"}`}
                          >
                            <div
                              className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                            >
                              <MapPin className="h-5 w-5" />
                            </div>
                            <div className="flex-1 text-sm">
                              {a.fullName && <div className="font-semibold">{a.fullName}</div>}
                              <div className="text-muted-foreground">
                                {a.line1}
                                {a.line2 ? `, ${a.line2}` : ""}
                              </div>
                              <div className="text-muted-foreground">
                                {a.postalCode} {a.city}
                              </div>
                              {a.phone && <div className="text-muted-foreground">📞 {a.phone}</div>}
                            </div>
                            {active && <Check className="h-5 w-5 shrink-0 text-primary" />}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Διάλεξε μία ή συμπλήρωσε νέα διεύθυνση παρακάτω.
                    </p>
                  </div>
                )}
                <div className="grid gap-3 sm:grid-cols-2">
                  <AddressAutocomplete
                    value={form.line1}
                    onChange={(v) => update("line1", v)}
                    onSelect={(addr) =>
                      setForm((f) => {
                        const postalCode = addr.postalCode || f.postalCode;
                        const isCy = /κυπρ|cyprus/i.test(addr.country || "");
                        const country = isCy ? "Κύπρος" : "Ελλάδα";
                        const region = isCy
                          ? f.region
                          : regionForGreekPostalCode(postalCode) || f.region;
                        return {
                          ...f,
                          line1: addr.line1 || f.line1,
                          city: addr.city || f.city,
                          postalCode,
                          region,
                          country,
                        };
                      })
                    }
                    className="sm:col-span-2"
                    error={fieldErrors.line1}
                  />
                  <Field
                    label="Πόλη"
                    required
                    value={form.city}
                    onChange={(v) => update("city", v)}
                    error={fieldErrors.city}
                    list="greek-cities"
                    placeholder="Ξεκίνα να πληκτρολογείς…"
                  />
                  <Field
                    label="Τ.Κ."
                    required
                    value={form.postalCode}
                    onChange={(v) => update("postalCode", v.replace(/\D/g, "").slice(0, 5))}
                    error={fieldErrors.postalCode}
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="5 ψηφία"
                  />
                  <SelectField
                    label="Νομός"
                    value={form.region}
                    onChange={(v) => update("region", v)}
                    placeholder="Επίλεξε νομό"
                    options={isCyprus ? [...CYPRUS_DISTRICTS] : [...GREEK_REGIONS]}
                  />
                  <SelectField
                    label="Χώρα"
                    required
                    value={form.country}
                    onChange={(v) => {
                      setForm((f) => ({ ...f, country: v, region: "" }));
                    }}
                    options={[...COUNTRIES]}
                  />
                  <datalist id="greek-cities">
                    {GREEK_CITIES.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>
              </Section>
            )}

            {maxStep >= 4 && (
              <Section
                icon={MessageCircle}
                title="Επικοινωνία σε περίπτωση ελλείψεων"
                step={4}
                total={6}
                active={activeStep === 4}
                done={activeStep !== 4}
                onEdit={() => goToStep(4)}
                onNext={() => goNext(4)}
              >
                <p className="mb-3 text-sm text-muted-foreground">
                  Επιλέξτε με ποιόν τρόπο θέλετε να προχωρήσουμε με την παραγγελία σας, σε περίπτωση
                  ελλείψεων.
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {OUT_OF_STOCK_OPTIONS.map((o) => {
                    const active = outOfStockAction === o.id;
                    return (
                      <label
                        key={o.id}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 p-3 transition ${active ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-primary/40"}`}
                      >
                        <input
                          type="radio"
                          name="oos-action"
                          checked={active}
                          onChange={() => {
                            setOutOfStockAction(o.id);
                            setFieldErrors((e) =>
                              e.outOfStockAction ? { ...e, outOfStockAction: "" } : e,
                            );
                          }}
                          className="sr-only"
                        />
                        <div
                          className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                        >
                          <MessageCircle className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold">{o.label}</div>
                          <div className="text-xs text-muted-foreground">{o.desc}</div>
                        </div>
                        {active && <Check className="h-5 w-5 shrink-0 text-primary" />}
                      </label>
                    );
                  })}
                </div>
                {fieldErrors.outOfStockAction && (
                  <span className="mt-2 block text-xs text-destructive">
                    {fieldErrors.outOfStockAction}
                  </span>
                )}
              </Section>
            )}

            {maxStep >= 5 && (
              <Section
                icon={Truck}
                title="Τρόπος αποστολής"
                step={5}
                total={6}
                active={activeStep === 5}
                done={activeStep !== 5}
                onEdit={() => goToStep(5)}
                onNext={() => goNext(5)}
              >
                {pickupInfo?.hasPreorder && (
                  <div className="mb-3 overflow-hidden rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-3">
                    <p className="flex items-center gap-2 text-sm font-extrabold text-amber-900">
                      <span className="text-lg">📦🐼</span> Η παραγγελία σου περιέχει προϊόν
                      «κατόπιν παραγγελίας»!
                    </p>
                    <p className="mt-1 text-sm font-medium text-amber-800">
                      Το φέρνουμε ειδικά για σένα, οπότε χρειαζόμαστε λίγο χρόνο ✨ Η παραγγελία
                      ετοιμάζεται σε <strong>3 εργάσιμες ημέρες</strong>. Γι' αυτό, για{" "}
                      <strong>παραλαβή από το κατάστημα</strong> ή{" "}
                      <strong>παράδοση MrPanda</strong> τα διαθέσιμα ραντεβού ξεκινούν μετά από 3
                      εργάσιμες.
                    </p>
                  </div>
                )}
                {hasFragileItem && (
                  <div className="mb-3 rounded-xl border-2 border-destructive/40 bg-destructive/5 p-3">
                    <p className="text-sm font-bold text-destructive">
                      ⚠️ Το καλάθι σου περιέχει εύθραυστο προϊόν
                    </p>
                    <p className="mt-1 text-sm text-foreground">
                      Λόγω ευθραυστότητας, δεν είναι δυνατή η αποστολή με courier. Επίλεξε{" "}
                      <strong>παραλαβή από το κατάστημα</strong> ή{" "}
                      <strong>παράδοση MrPanda εντός Θεσσαλονίκης</strong>.
                    </p>
                  </div>
                )}
                <div className="grid gap-2 sm:grid-cols-2">
                  {shownShippingMethods.map((s) => {
                    const active = shippingMethod === s.id;
                    const isSkroutzOverweight =
                      s.id === "skroutz_last_mile" &&
                      !!skroutz &&
                      !skroutz.serviceable &&
                      skroutz.totalWeightKg > skroutz.parcelMaxKg;
                    const isLockerBlocked = phoneIsLandline && LOCKER_METHODS.includes(s.id);
                    if (isSkroutzOverweight || isLockerBlocked) {

                      return (
                        <div
                          key={s.id}
                          className="flex cursor-not-allowed items-center gap-3 rounded-xl border-2 border-border p-3 opacity-50 blur-[0.5px]"
                        >
                          <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-border bg-white">
                            <img
                              src={SHIPPING_LOGOS[s.id]}
                              alt={`${s.label} logo`}
                              className="img-fit"
                              loading="lazy"
                            />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold">{s.label}</div>
                            <div className="text-xs text-muted-foreground">
                              {isLockerBlocked
                                ? "📵 Απαιτείται κινητό τηλέφωνο — η παράδοση σε θυρίδα δεν είναι διαθέσιμη με σταθερό τηλέφωνο."
                                : `⚖️ Η παραγγελία ξεπερνά το μέγιστο βάρος για Skroutz Last Mile (έως ${skroutz!.parcelMaxKg}kg ανά παραγγελία — η παραγγελία είναι ${skroutz!.totalWeightKg}kg). Διάλεξε άλλον τρόπο αποστολής.`}
                            </div>

                          </div>
                        </div>
                      );
                    }
                    return (
                      <label
                        key={s.id}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 p-3 transition ${active ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-primary/40"}`}
                      >
                        <input
                          type="radio"
                          name="sm"
                          checked={active}
                          onChange={() => setShippingMethod(s.id)}
                          className="sr-only"
                        />
                        <div
                          className={`relative h-11 w-11 shrink-0 overflow-hidden rounded-lg border bg-white ${active ? "border-primary" : "border-border"}`}
                        >
                          <img
                            src={SHIPPING_LOGOS[s.id]}
                            alt={`${s.label} logo`}
                            className="img-fit"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold">{s.label}</div>
                          <div className="text-xs text-muted-foreground">
                            {s.id === "store_pickup"
                              ? "Δωρεάν παραλαβή από το κατάστημά μας στη Θεσσαλονίκη — διαθέσιμη από παντού"
                              : s.desc}
                          </div>
                        </div>
                        {renderMethodCost(s.id)}
                        {active && <Check className="h-5 w-5 shrink-0 text-primary" />}
                      </label>
                    );
                  })}
                </div>
                {(isAcs || isElta || isCourierCenter) && (
                  <div className="mt-3 rounded-xl border-2 border-primary/50 bg-primary/5 p-3">
                    <label
                      htmlFor="courier-notes"
                      className="text-sm font-semibold text-foreground"
                    >
                      Σχόλια για την courier
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Σημειώστε εδώ πληροφορίες για την courier
                    </p>
                    <textarea
                      id="courier-notes"
                      value={courierNotes}
                      onChange={(e) => setCourierNotes(e.target.value)}
                      maxLength={300}
                      rows={3}
                      placeholder="π.χ. Χρόνος παράδοσης, οδηγίες κλπ."
                      className="mt-2 w-full rounded-lg border-2 border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                    />
                  </div>
                )}
                {shippingMethod === "customer_freight" && (
                  <div className="mt-3 rounded-xl border-2 border-primary/50 bg-primary/5 p-3">
                    <p className="text-sm font-bold text-foreground">
                      ⚠️ Το κόστος της αποστολής επιβαρύνει αποκλειστικά τον πελάτη.
                    </p>
                    <p className="mt-1 text-sm font-semibold text-foreground">
                      Υπόδειξέ μας τη μεταφορική εταιρεία που επιθυμείς για την παραλαβή της
                      παραγγελίας.
                    </p>
                    {!freightNeedsSuggestion && (
                      <div className="mt-3">
                        <label
                          htmlFor="freightCarrier"
                          className="text-sm font-semibold text-foreground"
                        >
                          Μεταφορική εταιρεία *
                        </label>
                        <input
                          id="freightCarrier"
                          type="text"
                          value={freightCarrier}
                          onChange={(e) => {
                            setFreightCarrier(e.target.value);
                            setFreightConfirmed(false);
                          }}
                          placeholder="Γράψε τη μεταφορική εταιρεία"
                          maxLength={120}
                          className="mt-1 w-full rounded-lg border-2 border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                        />
                        {freightCarrier.trim().length >= 3 &&
                          (freightConfirmed ? (
                            <p className="mt-2 text-sm font-semibold text-primary">
                              ✅ Αποθηκεύτηκε: {freightCarrier.trim()}
                            </p>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setFreightConfirmed(true)}
                              className="mt-2 w-full rounded-lg border-2 border-primary bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:opacity-90"
                            >
                              ✅ Αποθήκευση μεταφορικής
                            </button>
                          ))}
                      </div>
                    )}
                    {freightNeedsSuggestion ? (
                      <div className="mt-3 rounded-lg border-2 border-primary bg-background p-3">
                        <p className="text-sm font-semibold text-foreground">
                          🐼 Το MrPanda θα σου προτείνει συνεργαζόμενη μεταφορική εταιρεία και θα
                          επικοινωνήσει μαζί σου.
                        </p>
                        <button
                          type="button"
                          onClick={() => setFreightNeedsSuggestion(false)}
                          className="mt-2 text-sm font-semibold text-primary hover:underline"
                        >
                          ← Θα συμπληρώσω εγώ τη μεταφορική
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        disabled={freightCarrier.trim().length >= 3}
                        onClick={() => {
                          setFreightNeedsSuggestion(true);
                          setFreightCarrier("");
                          setFreightConfirmed(false);
                        }}
                        className="mt-3 w-full rounded-lg border-2 border-primary bg-background px-3 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-background"
                      >
                        Δεν υπάρχει συνεργαζόμενη μεταφορική εταιρία — να μου προτείνει το MrPanda
                      </button>
                    )}
                  </div>
                )}
                {shippingMethod === "store_pickup" && (
                  <div className="mt-3 rounded-xl border-2 border-primary/50 bg-primary/5 p-3">
                    <p className="text-sm font-bold text-foreground">
                      🏬 Παραλαβή από το κατάστημά μας
                    </p>
                    <p className="mt-1 text-sm text-foreground">
                      Π. Λεβαντή 15Α, 564 29 Σταυρούπολη Θεσσαλονίκης, Ελλάδα
                    </p>
                    <PickupSmsNotice className="mt-3" />
                    <a
                      href="https://www.google.com/maps/place//data=!4m2!3m1!1s0x14a8390054f97e2b:0xbcefb065218ddc4?sa=X&ved=1t:8290&ictx=111"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                    >
                      Άνοιξε στον χάρτη →
                    </a>
                    <button
                      type="button"
                      onClick={() => setPickupInfoOpen(true)}
                      className="mt-1 ml-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                    >
                      Στοιχεία & ωράριο καταστήματος
                    </button>
                    {pickupInfo && (
                      <PickupScheduler
                        info={pickupInfo}
                        value={pickupSlot}
                        error={pickupError}
                        onChange={(slot) => {
                          setPickupSlot(slot);
                          setPickupError(false);
                        }}
                      />
                    )}
                    {pickupInfo?.hasPreorder && (
                      <p className="mt-2 rounded-lg bg-amber-100 px-3 py-2 text-xs font-semibold text-amber-800">
                        📦 Η παραγγελία περιέχει προϊόν κατόπιν παραγγελίας.
                      </p>
                    )}
                    <div className="mt-2 space-y-1.5 text-xs font-medium text-muted-foreground">
                      <p>📱 Θα λάβεις SMS μόλις η παραγγελία είναι έτοιμη προς παραλαβή.</p>
                      <p>
                        ⏳ Η παραγγελία παραμένει στο κατάστημα από το επιλεγμένο παράθυρο και για
                        έως 5 εργάσιμες ημέρες.
                      </p>
                    </div>
                  </div>
                )}
                {isMrPanda && (
                  <div className="mt-3 rounded-xl border-2 border-primary/50 bg-primary/5 p-3">
                    <p className="text-sm font-bold text-foreground">
                      🐼 Παράδοση MrPanda — χρέωση βάσει χιλιομέτρων
                    </p>
                    {mrpandaLoading ? (
                      <p className="mt-1 text-sm text-muted-foreground">
                        Υπολογισμός απόστασης από το κατάστημα… ⏳
                      </p>
                    ) : !mrpanda ? (
                      <p className="mt-1 text-sm text-muted-foreground">
                        Συμπλήρωσε τη διεύθυνση αποστολής (οδό, Τ.Κ., πόλη) για να υπολογιστεί το
                        κόστος.
                      </p>
                    ) : mrpanda.serviceable ? (
                      <div className="mt-1 text-sm text-foreground">
                        <p>
                          Απόσταση από το κατάστημα:{" "}
                          <span className="font-semibold">{mrpanda.distanceKm} χλμ</span>
                        </p>
                        <p className="mt-0.5">
                          Μεταφορικά:{" "}
                          {mrpanda.cost === 0 ? (
                            <span className="font-bold text-success">Δωρεάν 🎉</span>
                          ) : (
                            <span className="font-bold">{formatPrice(mrpanda.cost)}</span>
                          )}
                        </p>
                        {mrpanda.cost > 0 && mrpanda.freeOver != null && (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            Δωρεάν μεταφορικά για αγορές άνω των {formatPrice(mrpanda.freeOver)} σε
                            αυτή την απόσταση.
                          </p>
                        )}
                        {pickupInfo && (
                          <DeliveryScheduler
                            info={pickupInfo}
                            distanceKm={mrpanda.distanceKm}
                            value={deliverySlot}
                            error={deliveryError}
                            onChange={(slot) => {
                              setDeliverySlot(slot);
                              setDeliveryError(false);
                            }}
                          />
                        )}
                        <div className="mt-2 space-y-1.5 text-xs font-medium text-muted-foreground">
                          <p>
                            📱 Θα ενημερωθείς μόλις η παραγγελία σου φύγει από τις εγκαταστάσεις
                            μας.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-1 text-sm font-semibold text-destructive">
                        {mrpanda.reason}
                      </p>
                    )}
                  </div>
                )}
                {isElta && (
                  <div className="mt-3 rounded-xl border-2 border-primary/50 bg-primary/5 p-3">
                    <p className="text-sm font-bold text-foreground">
                      📦 ΕΛΤΑ Courier — χρέωση βάσει βάρους
                    </p>
                    {eltaLoading ? (
                      <p className="mt-1 text-sm text-muted-foreground">
                        Υπολογισμός βάρους παραγγελίας… ⏳
                      </p>
                    ) : elta ? (
                      <div className="mt-1 text-sm text-foreground">
                        <p>
                          Συνολικό βάρος:{" "}
                          <span className="font-semibold">{elta.totalWeightKg} kg</span>{" "}
                          <span className="text-muted-foreground">
                            (χρέωση για {elta.billableKg} kg)
                          </span>
                        </p>
                        <p className="mt-0.5">
                          Μεταφορικά: <span className="font-bold">{formatPrice(elta.cost)}</span>
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {elta.isThessaloniki
                            ? "Θεσσαλονίκη: 2,50€ έως 2kg + 1€ κάθε επιπλέον κιλό."
                            : "Υπόλοιπη Ελλάδα: 3,50€ έως 2kg + 1,20€ κάθε επιπλέον κιλό."}
                        </p>
                      </div>
                    ) : (
                      <p className="mt-1 text-sm text-muted-foreground">
                        Συμπλήρωσε την πόλη για ακριβή χρέωση.
                      </p>
                    )}
                  </div>
                )}
                {isAcs && (
                  <div className="mt-3 rounded-xl border-2 border-primary/50 bg-primary/5 p-3">
                    <p className="text-sm font-bold text-foreground">
                      🚚 ACS Courier — χρέωση βάσει βάρους & προορισμού
                    </p>
                    {acsLoading ? (
                      <p className="mt-1 text-sm text-muted-foreground">Υπολογισμός χρέωσης… ⏳</p>
                    ) : acs ? (
                      <div className="mt-1 text-sm text-foreground">
                        <p>
                          Προορισμός: <span className="font-semibold">{acs.zoneLabel}</span>{" "}
                          <span className="text-muted-foreground">({acs.deliveryDays})</span>
                        </p>
                        {acs.remoteByApi && (
                          <p className="mt-0.5 text-xs font-semibold text-amber-600 dark:text-amber-400">
                            ⚠️ Δυσπρόσιτη περιοχή κατά ACS
                            {acs.remoteAreas.length > 0
                              ? `: ${acs.remoteAreas.slice(0, 3).join(", ")}`
                              : ""}
                          </p>
                        )}
                        <p className="mt-0.5">
                          Συνολικό βάρος:{" "}
                          <span className="font-semibold">{acs.totalWeightKg} kg</span>{" "}
                          <span className="text-muted-foreground">
                            (χρέωση για {acs.billableKg} kg)
                          </span>
                        </p>
                        <p className="mt-0.5">
                          Μεταφορικά: <span className="font-bold">{formatPrice(acs.cost)}</span>
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {formatPrice(acs.baseCost)} έως 3kg + {formatPrice(acs.extraRate)} κάθε
                          επιπλέον κιλό.
                        </p>
                      </div>
                    ) : (
                      <p className="mt-1 text-sm text-muted-foreground">
                        Συμπλήρωσε τον Τ.Κ. για ακριβή χρέωση.
                      </p>
                    )}
                  </div>
                )}
                {isCourierCenter && (
                  <div className="mt-3 rounded-xl border-2 border-primary/50 bg-primary/5 p-3">
                    <p className="text-sm font-bold text-foreground">
                      🚚 Courier Center — χρέωση βάσει βάρους & προορισμού
                    </p>
                    {courierCenterLoading ? (
                      <p className="mt-1 text-sm text-muted-foreground">Υπολογισμός χρέωσης… ⏳</p>
                    ) : courierCenter ? (
                      <div className="mt-1 text-sm text-foreground">
                        <p>
                          Προορισμός:{" "}
                          <span className="font-semibold">{courierCenter.zoneLabel}</span>{" "}
                          <span className="text-muted-foreground">
                            ({courierCenter.deliveryDays})
                          </span>
                        </p>
                        <p className="mt-0.5">
                          Συνολικό βάρος:{" "}
                          <span className="font-semibold">{courierCenter.totalWeightKg} kg</span>{" "}
                          <span className="text-muted-foreground">
                            (χρέωση για {courierCenter.billableKg} kg)
                          </span>
                        </p>
                        <p className="mt-0.5">
                          Μεταφορικά:{" "}
                          <span className="font-bold">{formatPrice(courierCenter.cost)}</span>
                        </p>
                        {courierCenter.cyprus ? (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {formatPrice(courierCenter.baseCost)} έως 2kg +{" "}
                            {formatPrice(courierCenter.extraRate)} κάθε επιπλέον κιλό (με ΦΠΑ).
                          </p>
                        ) : (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {formatPrice(courierCenter.baseCost)} έως 3kg +{" "}
                            {formatPrice(courierCenter.extraRate)} κάθε επιπλέον κιλό +{" "}
                            {formatPrice(courierCenter.perOrderFee)}/παραγγελία
                            {courierCenter.remoteSurcharge > 0
                              ? ` + ${formatPrice(courierCenter.remoteSurcharge)} δυσπρόσιτη περιοχή`
                              : ""}{" "}
                            + {courierCenter.energySurchargePct}% ενεργειακή κρίση.
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="mt-1 text-sm text-muted-foreground">
                        Συμπλήρωσε τον Τ.Κ. για ακριβή χρέωση.
                      </p>
                    )}
                  </div>
                )}
                {isBoxnow && (
                  <div className="mt-3 rounded-xl border-2 border-primary/50 bg-primary/5 p-3">
                    <p className="text-sm font-bold text-foreground">
                      📦 BoxNow — παράδοση σε θυρίδα (locker)
                    </p>
                    {boxnowLoading ? (
                      <p className="mt-1 text-sm text-muted-foreground">Υπολογισμός μεταφορικών… ⏳</p>
                    ) : boxnow && !boxnow.serviceable ? (
                      <p className="mt-1 text-sm font-semibold text-destructive">{boxnow.reason}</p>
                    ) : boxnow ? (
                      <div className="mt-1 text-sm text-foreground">
                        <p className="mt-0.5">
                          Μεταφορικά: <span className="font-bold">{formatPrice(boxnow.cost)}</span>
                        </p>
                      </div>
                    ) : null}
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() => setLockerOpen(true)}
                        className="mp-bouncy inline-flex items-center gap-2 rounded-full border border-primary bg-primary/5 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10"
                      >
                        <Package className="h-4 w-4" />{" "}
                        {locker ? "Αλλαγή θυρίδας" : "Επιλογή θυρίδας"}
                      </button>
                    </div>
                    {locker && (
                      <p className="mt-2 text-sm text-foreground">
                        ✅ Επιλεγμένη θυρίδα:{" "}
                        <span className="font-semibold">{locker.name ?? locker.id}</span>
                        {locker.address ? ` — ${locker.address}` : ""}
                        {locker.postcode ? `, ${locker.postcode}` : ""}
                      </p>
                    )}
                  </div>
                )}
                {isSkroutz && (
                  <div className="mt-3 rounded-xl border-2 border-primary/50 bg-primary/5 p-3">
                    <p className="text-sm font-bold text-foreground">
                      📦 Skroutz Last Mile — παράδοση σε θυρίδα
                    </p>
                    {skroutzLoading ? (
                      <p className="mt-1 text-sm text-muted-foreground">
                        Υπολογισμός μεταφορικών… ⏳
                      </p>
                    ) : skroutz && !skroutz.serviceable ? (
                      <p className="mt-1 text-sm font-semibold text-destructive">
                        {skroutz.reason}
                      </p>
                    ) : skroutz ? (
                      <div className="mt-1 text-sm text-foreground">
                        <p>
                          Συνολικό βάρος:{" "}
                          <span className="font-semibold">{skroutz.totalWeightKg} kg</span>{" "}
                          <span className="text-muted-foreground">
                            (1 δέμα έως {skroutz.parcelMaxKg}kg)
                          </span>
                        </p>
                        <p className="mt-0.5 text-muted-foreground">
                          Βασική χρέωση (με ΦΠΑ):{" "}
                          <span className="font-semibold">{formatPrice(skroutz.baseCost)}</span>
                          {skroutz.zoneSurcharge > 0 && (
                            <>
                              {" "}
                              {" + "}χρέωση ζώνης (με ΦΠΑ):{" "}
                              <span className="font-semibold">
                                {formatPrice(skroutz.zoneSurcharge)}
                              </span>
                            </>
                          )}
                        </p>
                        {!skroutzLocker && (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            Η τελική χρέωση ζώνης υπολογίζεται με βάση τον ΤΚ της θυρίδας που θα
                            επιλέξεις.
                          </p>
                        )}
                        <p className="mt-0.5">
                          Μεταφορικά: <span className="font-bold">{formatPrice(skroutz.cost)}</span>
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          📐 Μέγιστες διαστάσεις θυρίδας:{" "}
                          <span className="font-semibold text-foreground">
                            {SKROUTZ_LOCKER_DIMS}
                          </span>
                          . Σε περίπτωση επιστροφής της παραγγελίας από την courier με αιτιολογία
                          ότι το δέμα δεν χωράει στη θυρίδα {SKROUTZ_LOCKER_DIMS}, το κατάστημα δεν
                          φέρει ευθύνη.
                        </p>
                      </div>
                    ) : null}
                    {!skroutzLoading && (!skroutz || skroutz.serviceable) && (
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={() => setSkroutzLockerOpen(true)}
                          className="mp-bouncy inline-flex items-center gap-2 rounded-full border border-primary bg-primary/5 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10"
                        >
                          <PackageCheck className="h-4 w-4" />{" "}
                          {skroutzLocker ? "Αλλαγή θυρίδας" : "Επιλογή θυρίδας"}
                        </button>
                      </div>
                    )}
                    {skroutzLocker && skroutz?.serviceable && (
                      <p className="mt-2 text-sm text-foreground">
                        ✅ Επιλεγμένη θυρίδα:{" "}
                        <span className="font-semibold">
                          {skroutzLocker.name ?? skroutzLocker.id}
                        </span>
                        {skroutzLocker.address ? ` — ${skroutzLocker.address}` : ""}
                        {skroutzLocker.postcode ? `, ${skroutzLocker.postcode}` : ""}
                      </p>
                    )}
                  </div>
                )}
              </Section>
            )}

            {maxStep >= 6 && (
              <Section
                icon={Wallet}
                title="Τρόπος πληρωμής"
                step={6}
                total={6}
                active={activeStep === 6}
                done={activeStep !== 6}
                onEdit={() => goToStep(6)}
              >
                <div className="grid gap-2 sm:grid-cols-2">
                  {availablePaymentMethods.map((m) => {
                    const Icon = m.icon;
                    const active = method === m.id;
                    const logo = PAYMENT_LOGOS[m.id];
                    const disabled = m.disabled;
                    return (
                      <label
                        key={m.id}
                        className={`flex items-center gap-3 rounded-xl border-2 p-3 transition ${disabled ? "cursor-not-allowed opacity-40 blur-[1px]" : "cursor-pointer"} ${active && !disabled ? "border-primary bg-primary/5 shadow-sm" : "border-border"} ${!disabled && !active ? "hover:border-primary/40" : ""}`}
                      >
                        <input
                          type="radio"
                          name="pm"
                          checked={active}
                          disabled={disabled}
                          onChange={() => {
                            if (!disabled) setMethod(m.id);
                          }}
                          className="sr-only"
                        />
                        {logo ? (
                          <div
                            className={`relative h-11 w-11 shrink-0 overflow-hidden rounded-lg border bg-white ${active && !disabled ? "border-primary" : "border-border"}`}
                          >
                            <img
                              src={logo}
                              alt={`${m.label} logo`}
                              className="img-fit"
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <div
                            className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${active && !disabled ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                          >
                            <Icon className="h-5 w-5" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="text-sm font-semibold">{m.label}</div>
                          <div className="text-xs text-muted-foreground">{m.desc}</div>
                        </div>
                        {active && !disabled && <Check className="h-5 w-5 shrink-0 text-primary" />}
                      </label>
                    );
                  })}
                </div>
                {method === "bank_transfer" && (
                  <div className="mt-3 rounded-xl border-2 border-primary/50 bg-primary/5 p-4">
                    <p className="text-sm font-semibold text-foreground">
                      🏦 Κάνε την κατάθεση σε έναν από τους παρακάτω λογαριασμούς — Δικαιούχος:{" "}
                      <span className="font-bold">{BENEFICIARY}</span>
                    </p>
                    <p className="mt-1 text-sm font-bold text-primary">
                      Για πιο γρήγορη αποστολή της παραγγελίας, πείτε μας σε ποιον λογαριασμό θα
                      γίνει η κατάθεση.
                    </p>
                    <div className="mt-3 grid gap-2">
                      {BANK_ACCOUNTS.map((b) => {
                        const active = selectedBank === b.bank;
                        return (
                          <label
                            key={b.iban}
                            className={`flex cursor-pointer flex-col gap-0.5 rounded-lg border-2 p-3 transition sm:flex-row sm:items-center sm:justify-between ${active ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary/40"}`}
                          >
                            <span className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="bank-account"
                                checked={active}
                                onChange={() => setSelectedBank(b.bank)}
                                className="h-4 w-4 accent-primary"
                              />
                              <span className="text-sm font-semibold">{b.bank}</span>
                            </span>
                            <span className="flex items-center gap-1.5 pl-6 sm:pl-0">
                               <span className="selectable select-all font-mono text-xs text-muted-foreground sm:text-sm">
                                {b.iban}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(b.iban);
                                  setCopiedIban(b.iban);
                                  setTimeout(() => setCopiedIban(""), 2000);
                                }}
                                className="rounded-md p-1 text-muted-foreground transition hover:bg-primary/10 hover:text-primary"
                                title="Αντιγραφή IBAN"
                              >
                                {copiedIban === b.iban ? (
                                  <Check className="h-3.5 w-3.5 text-success" />
                                ) : (
                                  <Copy className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Section>
            )}

            {maxStep >= 6 && (
              <div>
              <button
                type="submit"
                form="checkout-form"
                disabled={loading || maxStep < 6 || activeStep < 6}
                title={
                  maxStep < 6 || activeStep < 6
                    ? "Ολοκλήρωσε πρώτα όλα τα βήματα της παραγγελίας"
                    : undefined
                }
                className="mp-bouncy group relative mt-4 flex w-full items-center justify-center gap-2.5 overflow-hidden rounded-full bg-gradient-to-r from-primary via-orange-500 to-amber-500 bg-[length:200%_auto] py-4 text-base font-bold text-primary-foreground shadow-lg shadow-primary/30 ring-1 ring-white/20 transition-all duration-300 hover:bg-[position:right_center] hover:shadow-xl hover:shadow-primary/40 active:scale-[0.98] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none disabled:grayscale"
              >
                {/* shine sweep */}
                <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Επεξεργασία...</span>
                  </>
                ) : (
                  <span>
                    {method === "cash"
                      ? "Ολοκλήρωση παραγγελίας"
                      : "Παραγγελία με υποχρέωση πληρωμής"}
                  </span>
                )}
              </button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
                <ShieldCheck className="h-4 w-4 text-success" /> Ασφαλής πληρωμή — συμφωνείς με τους
                όρους χρήσης.
              </p>
              <TrustBadges className="mt-4 border-t border-border pt-4" />
              </div>
            )}

            {err && (
              <div className="animate-fade-in rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                {err}
              </div>
            )}
          </form>

          <aside className="h-fit lg:sticky lg:top-24">
            <div className="relative overflow-hidden rounded-3xl border border-primary/20 bg-card p-5 shadow-lg shadow-primary/5">
              <span className="pointer-events-none absolute -right-3 -top-3 text-5xl opacity-10 animate-bob">
                🐼
              </span>
              <h2 className="relative mb-3 flex items-center gap-2 font-bold">
                <span className="grid h-9 w-9 place-items-center rounded-2xl bg-gradient-to-br from-primary to-amber-500 text-primary-foreground shadow-sm">
                  <ShoppingBag className="h-5 w-5" />
                </span>
                Η παραγγελία σου
              </h2>
              <ul className="mb-3 space-y-3 text-sm">
                {groupCartRows(items).map((row) => {
                  if (row.kind === "bundle")
                    return <BundleCartGroup key={row.key} group={row} readOnly />;
                  const it = row.item;
                  const onSale = it.originalPrice != null && it.originalPrice > it.price;
                  return (
                    <li
                      key={`${it.bundleId ?? ""}-${it.productId}`}
                      className={`relative flex items-center gap-3 rounded-lg p-1 ${onSale ? "mp-gold-card border border-yellow-400/60" : ""}`}
                    >
                      {onSale && (
                        <span className="mp-sparkle" style={{ top: "2px", left: "4px" }}>
                          ✨
                        </span>
                      )}
                      <Link
                        to="/p/$slug"
                        params={{ slug: productSlug(it.name, it.psId, it.productId) }}
                        className="relative grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-white"
                      >
                        {it.imageUrl ? (
                          <img
                            src={it.imageUrl}
                            alt={it.name}
                            className="img-fit"
                            draggable={false}
                          />
                        ) : (
                          <div className="grid h-full w-full place-items-center text-lg">🐼</div>
                        )}
                        <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                          {it.quantity}
                        </span>
                      </Link>
                      <Link
                        to="/p/$slug"
                        params={{ slug: productSlug(it.name, it.psId, it.productId) }}
                        className="line-clamp-2 flex-1 text-muted-foreground hover:text-primary"
                      >
                        {it.name}
                      </Link>
                      <span className="text-right">
                        {onSale && (
                          <span className="block text-[11px] text-muted-foreground line-through">
                            {formatPrice(it.originalPrice! * it.quantity)}
                          </span>
                        )}
                        <span className="block font-medium">
                          {formatPrice(it.price * it.quantity)}
                        </span>
                        {docType === "invoice" && (
                          <span className="block text-[11px] text-muted-foreground">
                            {formatPrice(netOf(it.price * it.quantity, itemRate(it)))} + ΦΠΑ{" "}
                            {itemRate(it)}%
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <dl className="space-y-1.5 border-t border-border pt-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">
                    Υποσύνολο{docType === "invoice" ? " (με ΦΠΑ)" : ""}
                  </dt>
                  <dd>{formatPrice(subtotal)}</dd>
                </div>
                {docType === "invoice" && (
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <dt>Υποσύνολο χωρίς ΦΠΑ</dt>
                    <dd>{formatPrice(subtotalNet)}</dd>
                  </div>
                )}
                {depositTotal > 0 && (
                  <div className="text-xs text-muted-foreground">
                    🍾 Περιλαμβάνεται εγγυοδοσία φιαλών {formatPrice(depositTotal)}
                  </div>
                )}
                {couponDiscount > 0 && (
                  <div className="flex justify-between">
                    <dt className="flex items-center gap-1 text-success">
                      🎟️ Έκπτωση{coupon ? ` (${coupon.code})` : ""}
                    </dt>
                    <dd className="font-semibold text-success">−{formatPrice(couponDiscount)}</dd>
                  </div>
                )}
                {bundleDiscount > 0 && (
                  <div className="flex justify-between">
                    <dt className="flex items-center gap-1 text-success">📦 Έκπτωση πακέτων</dt>
                    <dd className="font-semibold text-success">−{formatPrice(bundleDiscount)}</dd>
                  </div>
                )}
                {islandVatDiscount > 0 && (
                  <div className="flex justify-between">
                    <dt className="flex items-center gap-1 text-success">
                      🏝️ Μειωμένος ΦΠΑ{islandVat?.island ? ` (${islandVat.island})` : ""}
                    </dt>
                    <dd className="font-semibold text-success">
                      −{formatPrice(islandVatDiscount)}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="flex items-center gap-1 text-muted-foreground">
                    <Truck className="h-4 w-4" /> {isPickup ? "Παραλαβή" : "Μεταφορικά"}
                  </dt>
                  <dd>
                    {!shippingChosen ? (
                      <span className="text-xs font-medium text-muted-foreground">
                        Επίλεξε τρόπο αποστολής
                      </span>
                    ) : effectiveShipping === 0 ? (
                      <span className="font-semibold text-success">Δωρεάν 🎉</span>
                    ) : (
                      formatPrice(effectiveShipping)
                    )}
                  </dd>
                </div>
                {shippingChosen && docType === "invoice" && effectiveShipping > 0 && (
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <dt>Μεταφορικά χωρίς ΦΠΑ</dt>
                    <dd>{formatPrice(shippingNet)}</dd>
                  </div>
                )}
                {docType === "invoice" && (
                  <>
                    <div className="flex justify-between border-t border-border pt-2 text-sm">
                      <dt className="text-muted-foreground">Καθαρή αξία</dt>
                      <dd className="font-semibold">{formatPrice(totalNet)}</dd>
                    </div>
                    <div className="flex justify-between text-sm">
                      <dt className="text-muted-foreground">ΦΠΑ</dt>
                      <dd className="font-semibold">{formatPrice(vatAmount)}</dd>
                    </div>
                  </>
                )}
                <div className="flex justify-between border-t border-border pt-2 text-base font-bold">
                  <dt>Σύνολο{docType === "invoice" ? " (με ΦΠΑ)" : ""}</dt>
                  <dd className="text-price">
                    {formatPrice(shippingChosen ? effectiveTotal : effectiveTotal - effectiveShipping)}
                  </dd>
                </div>
              </dl>
              {/* Discount coupon */}
              <div className="mt-4 rounded-2xl border border-border bg-muted/30 p-3">
                {coupon ? (
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-success">
                      🎟️ {coupon.code}
                    </span>
                    <button
                      type="button"
                      onClick={removeCoupon}
                      className="text-xs font-medium text-muted-foreground underline-offset-2 hover:text-destructive hover:underline"
                    >
                      Αφαίρεση
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          applyCoupon();
                        }
                      }}
                      placeholder="Κωδικός κουπονιού"
                      className="min-w-0 flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm outline-none focus:border-primary"
                      aria-label="Κωδικός κουπονιού"
                    />
                    <button
                      type="button"
                      onClick={() => applyCoupon()}
                      disabled={couponLoading || !couponInput.trim()}
                      className="mp-bouncy shrink-0 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                    >
                      {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Εφαρμογή"}
                    </button>
                  </div>
                )}
                {couponMsg && (
                  <p
                    className={`mt-2 text-xs ${
                      couponMsg.type === "ok" ? "text-success" : "text-destructive"
                    }`}
                  >
                    {couponMsg.text}
                  </p>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
      <BoxnowLockerModal
        open={lockerOpen}
        onClose={() => setLockerOpen(false)}
        onSelect={(l) => {
          setLocker(l);
          setLockerOpen(false);
        }}
      />
      <SkroutzLockerModal
        open={skroutzLockerOpen}
        onClose={() => setSkroutzLockerOpen(false)}
        onSelect={(l) => {
          setSkroutzLocker(l);
          setSkroutzLockerOpen(false);
        }}
      />
      <SkroutzLockerNotice
        open={skroutzNoticeOpen}
        onAccept={() => {
          setSkroutzNoticeAccepted(true);
          setSkroutzNoticeOpen(false);
          setErr(null);
          setActiveStep(6);
          setMaxStep((m) => Math.max(m, 6));
          if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
      <Dialog open={landlineNoticeOpen} onOpenChange={setLandlineNoticeOpen}>
        <DialogContent className="max-w-md rounded-3xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 text-amber-950">
          <DialogHeader>
            <div className="mx-auto mb-1 grid h-16 w-16 place-items-center rounded-full bg-amber-200/70 text-3xl shadow-inner">
              ☎️
            </div>
            <DialogTitle className="text-center text-lg font-extrabold text-amber-900">
              Δώσε μας ένα κινητό για θυρίδα 📦
            </DialogTitle>
            <DialogDescription className="text-center text-sm font-medium text-amber-800">
              Ο αριθμός που συμπλήρωσες φαίνεται να είναι <strong>σταθερό τηλέφωνο</strong>. Οι
              θυρίδες <strong>BoxNow</strong> και <strong>Skroutz Last Mile</strong> στέλνουν τον
              κωδικό παραλαβής με SMS, οπότε δεν μπορούν να επιλεγούν.
            </DialogDescription>
          </DialogHeader>
          <p className="rounded-2xl bg-white/70 p-3 text-center text-sm text-amber-900">
            Βάλε ένα κινητό (69…) για να ξεκλειδώσεις τις θυρίδες, ή συνέχισε με courier /
            παραλαβή από το κατάστημα 🐼
          </p>
          <button
            type="button"
            onClick={() => setLandlineNoticeOpen(false)}
            className="mp-bouncy w-full rounded-full bg-amber-600 py-3 text-sm font-bold text-white hover:bg-amber-700"
          >
            Το κατάλαβα
          </button>
        </DialogContent>
      </Dialog>

      <StorePickupInfoDialog open={pickupInfoOpen} onOpenChange={setPickupInfoOpen} />
    </StorefrontLayout>
  );
}

function Section({
  icon: Icon,
  title,
  step,
  total = 3,
  active = true,
  done = false,
  onEdit,
  onNext,
  children,
}: {
  icon: typeof User;
  title: string;
  step: number;
  total?: number;
  active?: boolean;
  done?: boolean;
  onEdit?: () => void;
  onNext?: () => void;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`animate-pop-in rounded-3xl border bg-card p-5 transition-all ${active ? "border-primary/30 shadow-lg shadow-primary/5" : "border-border shadow-sm"}`}
    >
      <h2 className={`flex items-center gap-3 text-lg font-bold ${active ? "mb-4" : ""}`}>
        <span
          className={`relative grid h-10 w-10 shrink-0 place-items-center rounded-2xl shadow-sm transition-all ${done && !active ? "bg-success/20 text-success" : active ? "bg-gradient-to-br from-primary to-amber-500 text-primary-foreground animate-bob" : "bg-accent text-accent-foreground"}`}
        >
          {done && !active ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
          {!done && (
            <span className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-card text-[11px] font-extrabold text-primary shadow ring-1 ring-primary/20">
              {step}
            </span>
          )}
        </span>
        <span className="flex-1">{title}</span>
        {done && !active && onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="mp-bouncy rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-semibold text-primary transition hover:bg-primary/10"
          >
            ✏️ Αλλαγή
          </button>
        ) : (
          <span className="rounded-full bg-accent px-2.5 py-1 text-xs font-semibold text-accent-foreground">
            Βήμα {step}/{total}
          </span>
        )}
      </h2>
      {active && (
        <>
          {children}
          {onNext && (
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={onNext}
                className="mp-bouncy inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-primary to-amber-500 px-6 py-2.5 text-sm font-bold text-primary-foreground shadow-md shadow-primary/20 hover:opacity-95"
              >
                Επόμενο →
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  type = "text",
  className = "",
  inputMode,
  maxLength,
  placeholder,
  error,
  list,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  type?: string;
  className?: string;
  inputMode?: "text" | "numeric" | "tel" | "email" | "url" | "search" | "none" | "decimal";
  maxLength?: number;
  placeholder?: string;
  error?: string;
  list?: string;
}) {
  return (
    <label className={`block text-sm ${className}`}>
      <span className="mb-1 block font-medium text-muted-foreground">
        {label}
        {required && <span className="text-primary"> *</span>}
      </span>
      <input
        type={type}
        value={value}
        required={required}
        inputMode={inputMode}
        maxLength={maxLength}
        placeholder={placeholder}
        list={list}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-xl border bg-background px-3 py-2.5 outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 ${error ? "border-destructive" : "border-border"}`}
      />
      {error && <span className="mt-1 block text-xs text-destructive">{error}</span>}
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  required,
  className = "",
  placeholder,
  options,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  className?: string;
  placeholder?: string;
  options: string[];
  error?: string;
}) {
  return (
    <label className={`block text-sm ${className}`}>
      <span className="mb-1 block font-medium text-muted-foreground">
        {label}
        {required && <span className="text-primary"> *</span>}
      </span>
      <select
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-xl border bg-background px-3 py-2.5 outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 ${error ? "border-destructive" : "border-border"} ${value ? "" : "text-muted-foreground"}`}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o} value={o} className="text-foreground">
            {o}
          </option>
        ))}
      </select>
      {error && <span className="mt-1 block text-xs text-destructive">{error}</span>}
    </label>
  );
}
